import {
  Box,
  Button,
  Heading,
  Image,
  Text,
  Flex,
  Divider,
  useToast,
  Link as ChakraLink,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Badge,
  Collapse,
  useBreakpointValue,
  Input,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { Table, Thead, Tbody, Tr, Th, Td } from "@chakra-ui/react";
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown, ChevronUp, Trash } from "lucide-react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useJsApiLoader } from "@react-google-maps/api";

import { useAppSelector, useAppDispatch } from "../../store/hooks/hooks";
import {
  decreaseQuantity,
  increaseQuantity,
  removeFromCart,
} from "../../store/cartSlice/cartSlice";
import {
  CreateGroupedCheckoutPayload,
  SellerGroupedOrderPayload,
  useCreateGroupedCheckoutOrderMutation,
} from "../../orderApi/orderApi";
import { useAuth, UserAddress } from "../../auth/AuthContext";
import { db } from "../../configs/firebase";

import AddressList from "../../deliveryModule/components/AdressesList";
import AddAddressFlow from "../../deliveryModule/components/AddAddressFlow";
import OrderPricingBreakdownCard, {
  PricingBreakdown,
} from "./OrderPricingBreakdownCard";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import MapDisplay from "../../LocationsModule/MapDisplay";
import PaymentCheckoutTest from "./PaymentCheckoutTest";
import WhatsAppPhoneInput from "../../auth/WhatsappAuth/WhatsAppPhoneInput";
import { SupportedPhoneCountryCode } from "../../auth/WhatsappAuth/whatsappAuthHelpers";
import { claimPhoneNumberViaBackend } from "../../utils/firebaseHelpers";

type WarehouseItem = {
  id: string;
  name: string;
  description: string;
  address: string;
  lat: number;
  lng: number;
  photoURL?: string;
  createdAt: number;
  updatedAt?: number;
  isActive?: boolean;
  isDefaultDispatch?: boolean;
  supportsPickup?: boolean;
  supportsDelivery?: boolean;
};

type DeliveryQuote = {
  deliveryFee: number;
  distanceKm: number;
  billableWeightKg: number;
  bulkySurcharge: number;
};

type CustomerProfileData = {
  phone?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  businessPhoneNumber?: string;
  businessPhone?: string;
  displayName?: string;
  name?: string;
  email?: string;
  uid?: string;
  firebaseUid?: string;
};

type ClaimPhoneConflictError = Error & {
  code?: string;
  status?: number;
};

type ResolvedCustomerDetails = {
  name: string;
  phoneNumber: string;
};

type StoredGuestCheckoutAddress = {
  id: string;
  label: string;
  addressLine: string;
  latitude: number;
  longitude: number;
  isCurrent: boolean;
  isPickup: boolean;
  createdAt: number;
};

type StoredGuestCheckoutDetails = {
  name: string;
  phoneNumber: string;
  address: StoredGuestCheckoutAddress | null;
  updatedAt: number;
};

type StoredGuestCheckoutDetailsUpdate = {
  name?: string;
  phoneNumber?: string;
  address?: StoredGuestCheckoutAddress | null;
};

const GUEST_CHECKOUT_DETAILS_STORAGE_KEY = "pickdenguGuestCheckoutDetails";
const cardsBorderRadii = "8px";
const DELIVERY_ESTIMATE_MIN_DAYS = 5;
const DELIVERY_ESTIMATE_MAX_DAYS = 7;

const addCalendarDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setHours(12, 0, 0, 0);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const formatEstimatedDeliveryDate = (date: Date) =>
  new Intl.DateTimeFormat("en-MW", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);

const getEstimatedDeliveryRange = (purchaseDate: Date) => {
  const fromDate = addCalendarDays(purchaseDate, DELIVERY_ESTIMATE_MIN_DAYS);
  const toDate = addCalendarDays(purchaseDate, DELIVERY_ESTIMATE_MAX_DAYS);

  return {
    fromDate,
    toDate,
    label: `${formatEstimatedDeliveryDate(fromDate)} - ${formatEstimatedDeliveryDate(toDate)}`,
  };
};

const readGuestCheckoutDetails = (): StoredGuestCheckoutDetails | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(
      GUEST_CHECKOUT_DETAILS_STORAGE_KEY,
    );

    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<StoredGuestCheckoutDetails>;

    const parsedAddress = parsed.address;
    const hasValidAddress =
      !!parsedAddress &&
      typeof parsedAddress.addressLine === "string" &&
      Boolean(parsedAddress.addressLine.trim()) &&
      Number.isFinite(Number(parsedAddress.latitude)) &&
      Number.isFinite(Number(parsedAddress.longitude));

    return {
      name: String(parsed.name || "").trim(),
      phoneNumber: String(parsed.phoneNumber || "").replace(/[^\d]/g, ""),
      address: hasValidAddress
        ? {
            id: String(parsedAddress?.id || `guest-address-${Date.now()}`),
            label: String(parsedAddress?.label || "Delivery address"),
            addressLine: String(parsedAddress?.addressLine || "").trim(),
            latitude: Number(parsedAddress?.latitude),
            longitude: Number(parsedAddress?.longitude),
            isCurrent: true,
            isPickup: false,
            createdAt: Number(parsedAddress?.createdAt || Date.now()),
          }
        : null,
      updatedAt: Number(parsed.updatedAt || Date.now()),
    };
  } catch (error) {
    console.error("Failed to read guest checkout details:", error);

    return null;
  }
};

const saveGuestCheckoutDetails = (
  updates: StoredGuestCheckoutDetailsUpdate,
): StoredGuestCheckoutDetails | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const existing = readGuestCheckoutDetails();

  const hasAddressUpdate = Object.prototype.hasOwnProperty.call(
    updates,
    "address",
  );

  const nextDetails: StoredGuestCheckoutDetails = {
    name:
      updates.name !== undefined
        ? String(updates.name || "").trim()
        : String(
            existing?.name ||
              window.localStorage.getItem("customerName") ||
              window.localStorage.getItem("displayName") ||
              "",
          ).trim(),
    phoneNumber:
      updates.phoneNumber !== undefined
        ? String(updates.phoneNumber || "").replace(/[^\d]/g, "")
        : String(
            existing?.phoneNumber ||
              window.localStorage.getItem("phoneNumber") ||
              window.localStorage.getItem("whatsappNumber") ||
              window.localStorage.getItem("userPhoneNumber") ||
              "",
          ).replace(/[^\d]/g, ""),
    address: hasAddressUpdate
      ? updates.address || null
      : existing?.address || null,
    updatedAt: Date.now(),
  };

  window.localStorage.setItem(
    GUEST_CHECKOUT_DETAILS_STORAGE_KEY,
    JSON.stringify(nextDetails),
  );

  /*
   * Keep the existing keys for backward compatibility with other
   * parts of the application while the combined object remains the
   * primary source for guest checkout.
   */
  if (nextDetails.name) {
    window.localStorage.setItem("customerName", nextDetails.name);
    window.localStorage.setItem("displayName", nextDetails.name);
  }

  if (nextDetails.phoneNumber) {
    window.localStorage.setItem("phoneNumber", nextDetails.phoneNumber);
    window.localStorage.setItem("whatsappNumber", nextDetails.phoneNumber);
  }

  return nextDetails;
};

const DEFAULT_WHATSAPP_COUNTRY_CODE: SupportedPhoneCountryCode = "+265";
const GOOGLE_MAPS_LIBRARIES: "places"[] = ["places"];

type SellerCartGroup = {
  sellerUserId: string;
  sellerName: string;
  items: any[];
};

type SellerWarehouseState = {
  warehouses: WarehouseItem[];
  loading: boolean;
  error?: string;
};

type SellerCheckoutSummary = {
  group: SellerCartGroup;
  warehouses: WarehouseItem[];
  activeWarehouses: WarehouseItem[];
  deliveryWarehouses: WarehouseItem[];
  pickupWarehouses: WarehouseItem[];
  selectedPickupWarehouse: WarehouseItem | null;
  primaryDispatchWarehouse: WarehouseItem | null;
  deliveryQuote: DeliveryQuote | null;
  pricingBreakdown: PricingBreakdown;
  deliveryFeeReady: boolean;
  warehouseMessage: string;
};

const getVariantSummary = (item: any) => {
  if (
    !Array.isArray(item?.selectedVariants) ||
    item.selectedVariants.length === 0
  ) {
    return "";
  }

  return item.selectedVariants
    .map((variant: any) => {
      const name = variant.attributeName || variant.name || "Option";
      const value = variant.label || variant.value || "";
      return `${name}: ${value}`;
    })
    .join(" • ");
};

const roundMoney = (value: number) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const safeNumber = (value: any) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizePhone = (value?: string | null) =>
  String(value || "").replace(/[^\d]/g, "");

const getCountryDigits = (countryCode: SupportedPhoneCountryCode) =>
  normalizePhone(countryCode);

const buildFullPhone = (
  countryCode: SupportedPhoneCountryCode,
  phoneNumber: string,
) => {
  const countryDigits = getCountryDigits(countryCode);
  const localDigits = normalizePhone(phoneNumber).replace(/^0+/, "");

  if (!countryDigits || !localDigits) return "";

  return `${countryDigits}${localDigits}`;
};

const normalizeLocalPhoneToDefaultCountry = (value: any) => {
  const digits = normalizePhone(value);

  if (!digits) return "";

  const defaultCountryDigits = getCountryDigits(DEFAULT_WHATSAPP_COUNTRY_CODE);

  if (digits.length === 10 && digits.startsWith("0")) {
    return `${defaultCountryDigits}${digits.slice(1)}`;
  }

  return digits;
};

const normalizeStoredWhatsAppPhone = (value: any) => {
  const digits = normalizeLocalPhoneToDefaultCountry(value);

  if (!digits) return "";

  return digits;
};

const isValidWhatsAppFullPhone = (value: any) => {
  const digits = normalizePhone(value);

  if (digits.startsWith("27")) {
    return digits.length === 11;
  }

  if (digits.startsWith("265")) {
    return digits.length === 12;
  }

  return digits.length >= 10 && digits.length <= 15;
};

const getExpectedLocalPhoneLength = (
  countryCode: SupportedPhoneCountryCode,
) => {
  if (countryCode === "+27") return 9;
  if (countryCode === "+265") return 9;

  return 9;
};

const isValidLocalPhoneInput = (
  countryCode: SupportedPhoneCountryCode,
  phoneNumber: string,
) => {
  const digits = normalizePhone(phoneNumber).replace(/^0+/, "");
  const expectedLength = getExpectedLocalPhoneLength(countryCode);

  return digits.length >= expectedLength;
};

const getLocalPhoneValidationMessage = (
  countryCode: SupportedPhoneCountryCode,
  phoneNumber: string,
) => {
  const digits = normalizePhone(phoneNumber).replace(/^0+/, "");

  if (!digits) {
    return "WhatsApp number is required.";
  }

  if (countryCode === "+27" && digits.length < 9) {
    return "Enter the full South African number, for example 647367388.";
  }

  if (countryCode === "+265" && digits.length < 9) {
    return "Enter the full Malawi number without the country code.";
  }

  const fullPhone = buildFullPhone(countryCode, phoneNumber);

  if (!isValidWhatsAppFullPhone(fullPhone)) {
    return "Please check the country code and enter a valid WhatsApp number.";
  }

  return "";
};

const splitStoredPhoneToInput = (
  value: any,
): { countryCode: SupportedPhoneCountryCode; phoneNumber: string } => {
  const digits = normalizeStoredWhatsAppPhone(value);

  if (digits.startsWith("27")) {
    return {
      countryCode: "+27",
      phoneNumber: digits.slice(2),
    };
  }

  if (digits.startsWith("265")) {
    return {
      countryCode: "+265",
      phoneNumber: digits.slice(3),
    };
  }

  return {
    countryCode: DEFAULT_WHATSAPP_COUNTRY_CODE,
    phoneNumber: digits.startsWith("0") ? digits.slice(1) : digits,
  };
};

const getProfileName = (
  user: any,
  userSettings: any,
  profileData?: CustomerProfileData | null,
) =>
  String(
    profileData?.displayName ||
      profileData?.name ||
      userSettings?.displayName ||
      userSettings?.name ||
      user?.displayName ||
      localStorage.getItem("customerName") ||
      localStorage.getItem("displayName") ||
      String(user?.email || "").split("@")[0] ||
      "",
  ).trim();

const getProfilePhone = (
  user: any,
  userSettings: any,
  profileData?: CustomerProfileData | null,
) =>
  normalizeStoredWhatsAppPhone(
    profileData?.whatsappNumber ||
      profileData?.phoneNumber ||
      profileData?.phone ||
      profileData?.businessPhoneNumber ||
      profileData?.businessPhone ||
      userSettings?.whatsappNumber ||
      userSettings?.phoneNumber ||
      userSettings?.phone ||
      userSettings?.businessPhoneNumber ||
      userSettings?.businessPhone ||
      user?.phoneNumber ||
      user?.providerData?.[0]?.phoneNumber ||
      localStorage.getItem("phoneNumber") ||
      localStorage.getItem("whatsappNumber") ||
      localStorage.getItem("userPhoneNumber") ||
      "",
  );

const getCartItemSellerUserId = (item: any) =>
  String(
    item?.userId || item?.sellerUserId || item?.ownerId || item?.sellerId || "",
  ).trim();

const getCartItemSellerName = (item: any, sellerUserId: string) =>
  String(
    item?.sellerBusinessName ||
      item?.businessName ||
      item?.sellerName ||
      item?.shopName ||
      item?.ownerName ||
      sellerUserId ||
      "Seller",
  ).trim();

const groupCartBySeller = (items: any[]): SellerCartGroup[] => {
  const groupMap = new Map<string, SellerCartGroup>();

  (items || []).forEach((item, index) => {
    const sellerUserId = getCartItemSellerUserId(item);

    if (!sellerUserId) {
      const fallbackKey = `missing-seller-${item?._id || item?.productId || index}`;
      const existing = groupMap.get(fallbackKey);

      groupMap.set(fallbackKey, {
        sellerUserId: "",
        sellerName: "Missing seller",
        items: [...(existing?.items || []), item],
      });

      return;
    }

    const existing = groupMap.get(sellerUserId);

    if (existing) {
      existing.items.push(item);
      return;
    }

    groupMap.set(sellerUserId, {
      sellerUserId,
      sellerName: getCartItemSellerName(item, sellerUserId),
      items: [item],
    });
  });

  return Array.from(groupMap.values());
};

const normalizeWarehouse = (warehouse: any): WarehouseItem | null => {
  if (!warehouse) return null;

  const id = String(warehouse.id || warehouse._id || "").trim();
  const name = String(warehouse.name || "Warehouse").trim();
  const address = String(warehouse.address || "").trim();

  const lat = Number(warehouse.lat);
  const lng = Number(warehouse.lng);

  if (!id || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return {
    id,
    name,
    description: String(warehouse.description || "").trim(),
    address,
    lat,
    lng,
    photoURL: warehouse.photoURL || "",
    createdAt: Number(warehouse.createdAt || Date.now()),
    updatedAt: warehouse.updatedAt ? Number(warehouse.updatedAt) : undefined,
    isActive: warehouse.isActive !== false,
    isDefaultDispatch: warehouse.isDefaultDispatch === true,
    supportsPickup: warehouse.supportsPickup !== false,
    supportsDelivery: warehouse.supportsDelivery !== false,
  };
};

const getWarehouseMessage = (
  group: SellerCartGroup,
  warehousesLoading: boolean,
  warehouses: WarehouseItem[],
  activeWarehouses: WarehouseItem[],
  deliveryWarehouses: WarehouseItem[],
  fulfilmentType: "delivery" | "pickup",
  pickupWarehouses: WarehouseItem[],
) => {
  if (!group.sellerUserId)
    return "Some cart items are missing seller information";
  if (warehousesLoading) return "Loading seller warehouse";
  if (warehouses.length === 0) return "Seller has not added any warehouse yet";
  if (activeWarehouses.length === 0) return "Seller warehouses are inactive";

  if (fulfilmentType === "delivery" && deliveryWarehouses.length === 0) {
    return "Seller has no active delivery warehouse";
  }

  if (fulfilmentType === "pickup" && pickupWarehouses.length === 0) {
    return "Seller has no active pickup warehouse";
  }

  return "";
};

const haversineKm = (
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number },
) => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const radiusKm = 6371;

  const dLat = toRad(destination.lat - origin.lat);
  const dLng = toRad(destination.lng - origin.lng);

  const lat1 = toRad(origin.lat);
  const lat2 = toRad(destination.lat);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return radiusKm * c;
};

const getDistanceBandMultiplier = (distanceKm: number) => {
  if (distanceKm <= 5) return 1;
  if (distanceKm <= 10) return 1.08;
  if (distanceKm <= 20) return 1.15;
  if (distanceKm <= 40) return 1.25;
  return 1.4;
};

const calculateBillableWeightKg = (items: any[]) => {
  const totalActualWeightKg = (items || []).reduce((sum, item) => {
    const qty = Math.max(1, safeNumber(item.quantity));
    const weightKg = safeNumber(item.weightKg);
    return sum + weightKg * qty;
  }, 0);

  const totalVolumetricWeightKg = (items || []).reduce((sum, item) => {
    const qty = Math.max(1, safeNumber(item.quantity));
    const lengthCm = safeNumber(item.lengthCm);
    const widthCm = safeNumber(item.widthCm);
    const heightCm = safeNumber(item.heightCm);

    if (!lengthCm || !widthCm || !heightCm) return sum;

    const volumetric = (lengthCm * widthCm * heightCm) / 5000;
    return sum + volumetric * qty;
  }, 0);

  return roundMoney(Math.max(1, totalActualWeightKg, totalVolumetricWeightKg));
};

const calculateDeliveryQuote = (
  items: any[],
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number },
): DeliveryQuote => {
  const DELIVERY_BASE_FEE = 1500;
  const DELIVERY_PER_KM_RATE = 120;
  const DELIVERY_WEIGHT_RATE_PER_KG = 250;
  const BULKY_SURCHARGE = 2000;

  const distanceKm = roundMoney(haversineKm(origin, destination));
  const billableWeightKg = calculateBillableWeightKg(items);
  const hasBulkyItem = (items || []).some((item) => Boolean(item?.bulky));
  const bulkySurcharge = hasBulkyItem ? BULKY_SURCHARGE : 0;

  const distanceBandMultiplier = getDistanceBandMultiplier(distanceKm);

  const baseWithDistance = roundMoney(
    (DELIVERY_BASE_FEE + distanceKm * DELIVERY_PER_KM_RATE) *
      distanceBandMultiplier,
  );

  const weightCharge = roundMoney(
    billableWeightKg * DELIVERY_WEIGHT_RATE_PER_KG,
  );

  const deliveryFee = roundMoney(
    baseWithDistance + weightCharge + bulkySurcharge,
  );

  return {
    deliveryFee,
    distanceKm,
    billableWeightKg,
    bulkySurcharge,
  };
};

const calculateLocalBreakdown = (
  items: any[],
  fulfilmentType: "delivery" | "pickup",
  deliveryQuote: DeliveryQuote | null,
): PricingBreakdown => {
  const productSubtotal = roundMoney(
    items.reduce((sum, item) => {
      return (
        sum +
        Number(item.selectedPrice ?? item.price ?? 0) *
          Number(item.quantity || 0)
      );
    }, 0),
  );

  const serviceFee =
    productSubtotal > 0 ? roundMoney(Math.max(productSubtotal * 0.05, 500)) : 0;

  const deliveryFee =
    fulfilmentType === "delivery" && deliveryQuote
      ? Number(deliveryQuote.deliveryFee || 0)
      : 0;

  const discountAmount = 0;

  const total = roundMoney(
    productSubtotal + serviceFee + deliveryFee - discountAmount,
  );

  return {
    currency: "MWK",
    productSubtotal,
    serviceFee,
    deliveryFee,
    discountAmount,
    total,
    deliveryDistanceKm: Number(deliveryQuote?.distanceKm || 0),
    billableWeightKg: Number(deliveryQuote?.billableWeightKg || 0),
    bulkySurcharge: Number(deliveryQuote?.bulkySurcharge || 0),
  };
};

type SelectedAddressAutocompleteProps = {
  placeholder: string;
  initialValue?: string;
  inputStyles?: Record<string, any>;
  onInputChanged: (value: string) => void;
  onAddressSelected: (
    address: string,
    coords: { lat: number; lng: number },
  ) => void;
};

const SelectedAddressAutocomplete = ({
  placeholder,
  initialValue = "",
  inputStyles,
  onInputChanged,
  onAddressSelected,
}: SelectedAddressAutocompleteProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onInputChangedRef = useRef(onInputChanged);
  const onAddressSelectedRef = useRef(onAddressSelected);
  const selectingSuggestionRef = useRef(false);
  const commitTimersRef = useRef<number[]>([]);

  useEffect(() => {
    onInputChangedRef.current = onInputChanged;
    onAddressSelectedRef.current = onAddressSelected;
  }, [onAddressSelected, onInputChanged]);

  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== initialValue) {
      inputRef.current.value = initialValue;
    }
  }, [initialValue]);

  useEffect(() => {
    let disposed = false;
    let retryTimer: number | null = null;
    let placeChangedListener: google.maps.MapsEventListener | null = null;
    let pacObserver: MutationObserver | null = null;
    let pacStyle: HTMLStyleElement | null = null;

    const clearCommitTimers = () => {
      commitTimersRef.current.forEach((timer) => {
        window.clearTimeout(timer);
      });
      commitTimersRef.current = [];
    };

    const applyPacContainerFix = () => {
      const containers =
        document.querySelectorAll<HTMLElement>(".pac-container");

      containers.forEach((container) => {
        container.style.setProperty("z-index", "2147483647", "important");
        container.style.setProperty("pointer-events", "auto", "important");
        container.style.setProperty("-webkit-overflow-scrolling", "touch");
        container.style.setProperty("overscroll-behavior", "contain");
      });
    };

    const commitCurrentPlace = () => {
      if (disposed || !autocompleteRef.current) {
        return false;
      }

      const place = autocompleteRef.current.getPlace();
      const location = place?.geometry?.location;
      const selectedAddress = String(
        place?.formatted_address ||
          place?.name ||
          inputRef.current?.value ||
          "",
      ).trim();

      if (!selectedAddress || !location) {
        return false;
      }

      const selectedCoords = {
        lat: Number(location.lat()),
        lng: Number(location.lng()),
      };

      if (
        !Number.isFinite(selectedCoords.lat) ||
        !Number.isFinite(selectedCoords.lng)
      ) {
        return false;
      }

      if (inputRef.current) {
        inputRef.current.value = selectedAddress;
      }

      onAddressSelectedRef.current(selectedAddress, selectedCoords);
      selectingSuggestionRef.current = false;
      return true;
    };

    const schedulePlaceCommit = () => {
      clearCommitTimers();

      [0, 80, 180, 350].forEach((delay) => {
        const timer = window.setTimeout(() => {
          commitCurrentPlace();
        }, delay);

        commitTimersRef.current.push(timer);
      });
    };

    const isPacItemTarget = (target: EventTarget | null) => {
      return target instanceof Element && Boolean(target.closest(".pac-item"));
    };

    const handleSuggestionPointerDown = (event: Event) => {
      if (!isPacItemTarget(event.target)) {
        return;
      }

      selectingSuggestionRef.current = true;
      applyPacContainerFix();
    };

    const handleSuggestionPointerUp = (event: Event) => {
      if (!isPacItemTarget(event.target)) {
        return;
      }

      selectingSuggestionRef.current = true;
      schedulePlaceCommit();
    };

    const initializeAutocomplete = () => {
      if (disposed) {
        return;
      }

      if (!inputRef.current || !window.google?.maps?.places?.Autocomplete) {
        retryTimer = window.setTimeout(initializeAutocomplete, 250);
        return;
      }

      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: [
            "formatted_address",
            "geometry",
            "name",
            "place_id",
            "address_components",
          ],
          types: ["geocode"],
          componentRestrictions: {
            country: "mw",
          },
        },
      );

      placeChangedListener = autocompleteRef.current.addListener(
        "place_changed",
        () => {
          schedulePlaceCommit();
        },
      );

      applyPacContainerFix();
    };

    pacStyle = document.createElement("style");
    pacStyle.setAttribute("data-checkout-google-autocomplete", "true");
    pacStyle.textContent = `
      .pac-container {
        z-index: 2147483647 !important;
        pointer-events: auto !important;
        -webkit-overflow-scrolling: touch !important;
        overscroll-behavior: contain !important;
      }

      .pac-container .pac-item {
        min-height: 46px;
        padding-top: 10px;
        padding-bottom: 10px;
        line-height: 22px;
        touch-action: manipulation;
      }
    `;
    document.head.appendChild(pacStyle);

    pacObserver = new MutationObserver(() => {
      applyPacContainerFix();
    });
    pacObserver.observe(document.body, { childList: true });

    document.addEventListener("pointerdown", handleSuggestionPointerDown, true);
    document.addEventListener("pointerup", handleSuggestionPointerUp, true);
    document.addEventListener("touchstart", handleSuggestionPointerDown, true);
    document.addEventListener("touchend", handleSuggestionPointerUp, true);

    initializeAutocomplete();

    return () => {
      disposed = true;
      clearCommitTimers();

      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
      }

      document.removeEventListener(
        "pointerdown",
        handleSuggestionPointerDown,
        true,
      );
      document.removeEventListener(
        "pointerup",
        handleSuggestionPointerUp,
        true,
      );
      document.removeEventListener(
        "touchstart",
        handleSuggestionPointerDown,
        true,
      );
      document.removeEventListener("touchend", handleSuggestionPointerUp, true);

      pacObserver?.disconnect();
      pacStyle?.remove();

      if (placeChangedListener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(placeChangedListener);
      }

      autocompleteRef.current = null;
    };
  }, []);

  return (
    <Input
      ref={inputRef}
      defaultValue={initialValue}
      placeholder={placeholder}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="none"
      spellCheck={false}
      fontSize={{ base: "16px", md: "inherit" }}
      {...(inputStyles || {})}
      onFocus={() => {
        window.requestAnimationFrame(() => {
          const containers =
            document.querySelectorAll<HTMLElement>(".pac-container");

          containers.forEach((container) => {
            container.style.setProperty("z-index", "2147483647", "important");
            container.style.setProperty("pointer-events", "auto", "important");
          });
        });
      }}
      onBlur={() => {
        if (!selectingSuggestionRef.current) {
          return;
        }

        window.setTimeout(() => {
          const place = autocompleteRef.current?.getPlace();
          const location = place?.geometry?.location;

          if (!location) {
            return;
          }

          const selectedAddress = String(
            place?.formatted_address ||
              place?.name ||
              inputRef.current?.value ||
              "",
          ).trim();

          if (!selectedAddress) {
            return;
          }

          const selectedCoords = {
            lat: Number(location.lat()),
            lng: Number(location.lng()),
          };

          if (
            Number.isFinite(selectedCoords.lat) &&
            Number.isFinite(selectedCoords.lng)
          ) {
            if (inputRef.current) {
              inputRef.current.value = selectedAddress;
            }

            onAddressSelectedRef.current(selectedAddress, selectedCoords);
          }

          selectingSuggestionRef.current = false;
        }, 220);
      }}
      onChange={(event) => {
        selectingSuggestionRef.current = false;
        onInputChangedRef.current(event.target.value);
      }}
    />
  );
};

const CheckoutPage = () => {
  const cart = useAppSelector((state) => state.cart);
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { user, userSettings } = useAuth();
  const { theme } = useCustomTheme();

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;

  const { isLoaded: isGoogleMapsLoaded, loadError: googleMapsLoadError } =
    useJsApiLoader({
      googleMapsApiKey: GOOGLE_MAPS_API_KEY,
      libraries: GOOGLE_MAPS_LIBRARIES,
    });

  const [createGroupedCheckoutOrder] = useCreateGroupedCheckoutOrderMutation();

  const isDark = theme.name === "dark";

  const pageText = theme.text;
  const mutedText = theme.textMuted;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;
  const dividerColor = theme.divider;
  const panelShadow = theme.shadowMd;
  const cardShadow = theme.shadowSm;

  const estimatedDeliveryRange = useMemo(
    () => getEstimatedDeliveryRange(new Date()),
    [],
  );

  const secondaryButtonStyles = {
    color: pageText,
    bg: isDark ? "rgba(255,255,255,0.04)" : "white",
    border: `1px solid ${borderColor}`,
    _hover: {
      bg: isDark ? "rgba(255,255,255,0.07)" : "rgba(42,44,59,0.03)",
      borderColor: borderStrong,
    },
    borderRadius: { cardsBorderRadii },
    transition: "all 0.2s ease",
  };

  const addresses = userSettings?.addresses ?? [];

  const sellerGroups = useMemo(
    () => groupCartBySeller(cart.items as any[]),
    [cart.items],
  );

  const [warehouseStateBySeller, setWarehouseStateBySeller] = useState<
    Record<string, SellerWarehouseState>
  >({});

  const sellerWarehouseIds = useMemo(
    () =>
      Array.from(
        new Set(
          sellerGroups.map((group) => group.sellerUserId).filter(Boolean),
        ),
      ),
    [sellerGroups],
  );

  /*
   * An uninitialized seller entry is still loading. This closes the initial
   * render gap before the warehouse-fetch effect marks each seller as loading.
   */
  const warehousesLoading =
    sellerWarehouseIds.length > 0 &&
    sellerWarehouseIds.some(
      (sellerId) => warehouseStateBySeller[sellerId]?.loading !== false,
    );

  const [selectedPickupIds, setSelectedPickupIds] = useState<
    Record<string, string | null>
  >({});

  const [selectedAddress, setSelectedAddress] = useState<UserAddress | null>(
    addresses.find((a) => a.isCurrent) ?? null,
  );

  const [guestAddressLabel, setGuestAddressLabel] =
    useState("Delivery address");
  const [guestAddressLine, setGuestAddressLine] = useState("");
  const [guestAddressCoords, setGuestAddressCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [guestAddressTouched, setGuestAddressTouched] = useState(false);
  const [guestAddressInputKey, setGuestAddressInputKey] = useState(0);
  const [hasConfirmedGuestAddress, setHasConfirmedGuestAddress] =
    useState(false);
  const [guestAddressEditingExisting, setGuestAddressEditingExisting] =
    useState(false);

  const [profileLoading, setProfileLoading] = useState(false);
  const [customerProfile, setCustomerProfile] =
    useState<CustomerProfileData | null>(null);
  const [customerNameInput, setCustomerNameInput] = useState("");
  const [customerNameTouched, setCustomerNameTouched] = useState(false);
  const [savedCustomerPhone, setSavedCustomerPhone] = useState("");
  const [invalidSavedCustomerPhone, setInvalidSavedCustomerPhone] =
    useState("");
  const [countryCode, setCountryCode] = useState<SupportedPhoneCountryCode>(
    DEFAULT_WHATSAPP_COUNTRY_CODE,
  );
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [phoneNumberTouched, setPhoneNumberTouched] = useState(false);
  const [customerDetailsSaveSuccess, setCustomerDetailsSaveSuccess] =
    useState(false);
  const [savingCustomerDetails, setSavingCustomerDetails] = useState(false);
  const customerDetailsRef = useRef<HTMLDivElement | null>(null);
  const [highlightCustomerDetails, setHighlightCustomerDetails] =
    useState(false);

  const [showAddAddress, setShowAddAddress] = useState(false);
  const addAddressFormRef = useRef<HTMLDivElement | null>(null);
  const [highlightAddAddressForm, setHighlightAddAddressForm] = useState(false);

  const [fulfilmentType, setFulfilmentType] = useState<"delivery" | "pickup">(
    "delivery",
  );

  const defaultShowCheckoutDetails = useBreakpointValue({
    base: false,
    md: true,
  });

  const [showCheckoutDetails, setShowCheckoutDetails] = useState(false);

  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [center, setCenter] = useState({ lat: -13.9626, lng: 33.7741 });

  useEffect(() => {
    if (typeof defaultShowCheckoutDetails === "boolean") {
      setShowCheckoutDetails(defaultShowCheckoutDetails);
    }
  }, [defaultShowCheckoutDetails]);

  useEffect(() => {
    const loadCustomerDetails = async () => {
      if (!user?.uid) {
        /*
         * For guest checkout, the combined object is the primary
         * source. Legacy individual keys remain only as fallbacks.
         */
        const storedGuestDetails = readGuestCheckoutDetails();

        const guestProfile: CustomerProfileData = {
          displayName:
            storedGuestDetails?.name ||
            localStorage.getItem("customerName") ||
            localStorage.getItem("displayName") ||
            "",
          phoneNumber:
            storedGuestDetails?.phoneNumber ||
            localStorage.getItem("phoneNumber") ||
            localStorage.getItem("whatsappNumber") ||
            localStorage.getItem("userPhoneNumber") ||
            "",
        };

        setProfileLoading(false);

        const guestName = getProfileName(null, null, guestProfile);
        const guestPhone = getProfilePhone(null, null, guestProfile);
        const splitPhone = splitStoredPhoneToInput(guestPhone);

        setCustomerProfile(guestProfile);
        setCustomerNameInput(guestName);
        setSavedCustomerPhone(
          guestPhone && isValidWhatsAppFullPhone(guestPhone) ? guestPhone : "",
        );
        setInvalidSavedCustomerPhone(
          guestPhone && !isValidWhatsAppFullPhone(guestPhone) ? guestPhone : "",
        );
        setCountryCode(splitPhone.countryCode);
        setPhoneNumberInput(splitPhone.phoneNumber);
        setPhoneNumberTouched(false);
        setCustomerNameTouched(false);
        setCustomerDetailsSaveSuccess(Boolean(guestName && guestPhone));

        const storedAddress = storedGuestDetails?.address || null;

        if (storedAddress) {
          const restoredAddress: UserAddress = {
            id: storedAddress.id,
            label: storedAddress.label || "Delivery address",
            addressLine: storedAddress.addressLine,
            latitude: storedAddress.latitude,
            longitude: storedAddress.longitude,
            isCurrent: true,
            isPickup: false,
            createdAt: storedAddress.createdAt || Date.now(),
          };

          setSelectedAddress(restoredAddress);
          setGuestAddressLabel(restoredAddress.label || "Delivery address");
          setGuestAddressLine(restoredAddress.addressLine);
          setGuestAddressCoords({
            lat: restoredAddress.latitude,
            lng: restoredAddress.longitude,
          });
          setGuestAddressTouched(false);
          setHasConfirmedGuestAddress(true);
          setGuestAddressEditingExisting(false);
          setGuestAddressInputKey((currentKey) => currentKey + 1);
        } else {
          setSelectedAddress(null);
          setGuestAddressLabel("Delivery address");
          setGuestAddressLine("");
          setGuestAddressCoords(null);
          setGuestAddressTouched(false);
          setHasConfirmedGuestAddress(false);
          setGuestAddressEditingExisting(false);
        }

        /*
         * Migrate any old guest name/number values into the single
         * object without changing their existing behaviour.
         */
        saveGuestCheckoutDetails({
          name: guestName,
          phoneNumber: guestPhone,
          address: storedAddress,
        });

        return;
      }

      setProfileLoading(true);

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const profileData = userSnap.exists()
          ? (userSnap.data() as CustomerProfileData)
          : null;

        const profileName = getProfileName(user, userSettings, profileData);
        const profilePhone = getProfilePhone(user, userSettings, profileData);
        const splitPhone = splitStoredPhoneToInput(profilePhone);

        setCustomerProfile(profileData);
        setCustomerNameInput(profileName);
        setSavedCustomerPhone(
          profilePhone && isValidWhatsAppFullPhone(profilePhone)
            ? profilePhone
            : "",
        );
        setInvalidSavedCustomerPhone(
          profilePhone && !isValidWhatsAppFullPhone(profilePhone)
            ? profilePhone
            : "",
        );
        setCountryCode(splitPhone.countryCode);
        setPhoneNumberInput(splitPhone.phoneNumber);
        setPhoneNumberTouched(false);
        setCustomerNameTouched(false);
        setCustomerDetailsSaveSuccess(false);
      } catch (error) {
        console.error("Failed to fetch customer profile:", error);

        const fallbackProfile: CustomerProfileData = {
          displayName: getProfileName(user, userSettings, null),
          phoneNumber: getProfilePhone(user, userSettings, null),
        };
        const fallbackPhone = getProfilePhone(
          user,
          userSettings,
          fallbackProfile,
        );
        const splitPhone = splitStoredPhoneToInput(fallbackPhone);

        setCustomerProfile(fallbackProfile);
        setCustomerNameInput(
          getProfileName(user, userSettings, fallbackProfile),
        );
        setSavedCustomerPhone(
          fallbackPhone && isValidWhatsAppFullPhone(fallbackPhone)
            ? fallbackPhone
            : "",
        );
        setInvalidSavedCustomerPhone(
          fallbackPhone && !isValidWhatsAppFullPhone(fallbackPhone)
            ? fallbackPhone
            : "",
        );
        setCountryCode(splitPhone.countryCode);
        setPhoneNumberInput(splitPhone.phoneNumber);

        toast({
          title: "Could not load profile details",
          description:
            "You can still enter your name and WhatsApp number before checkout.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setProfileLoading(false);
      }
    };

    loadCustomerDetails();
  }, [user?.uid, userSettings, toast]);

  useEffect(() => {
    if (!user?.uid) {
      return;
    }

    const current = addresses.find((a) => a.isCurrent);
    if (current) {
      setSelectedAddress(current);
    } else if (
      !current &&
      selectedAddress &&
      !addresses.find((a) => a.id === selectedAddress.id)
    ) {
      setSelectedAddress(addresses[0] ?? null);
    }
  }, [addresses, user?.uid]);

  useEffect(() => {
    const fetchSellerWarehouses = async () => {
      const sellerIds = Array.from(
        new Set(
          sellerGroups.map((group) => group.sellerUserId).filter(Boolean),
        ),
      );

      if (sellerIds.length === 0) {
        setWarehouseStateBySeller({});
        return;
      }

      setWarehouseStateBySeller((prev) => {
        const next = { ...prev };

        sellerIds.forEach((sellerId) => {
          next[sellerId] = {
            warehouses: prev[sellerId]?.warehouses || [],
            loading: true,
            error: undefined,
          };
        });

        return next;
      });

      await Promise.all(
        sellerIds.map(async (sellerId) => {
          try {
            const docSnap = await getDoc(doc(db, "users", sellerId));

            if (docSnap.exists()) {
              const data = docSnap.data();
              const rawWarehouses = Array.isArray(data.warehouses)
                ? data.warehouses
                : [];

              const warehouses = rawWarehouses
                .map(normalizeWarehouse)
                .filter(Boolean) as WarehouseItem[];

              setWarehouseStateBySeller((prev) => ({
                ...prev,
                [sellerId]: {
                  warehouses,
                  loading: false,
                  error: undefined,
                },
              }));

              return;
            }

            setWarehouseStateBySeller((prev) => ({
              ...prev,
              [sellerId]: {
                warehouses: [],
                loading: false,
                error: "Seller profile not found",
              },
            }));
          } catch (error) {
            console.error("Failed to fetch seller warehouses:", error);

            setWarehouseStateBySeller((prev) => ({
              ...prev,
              [sellerId]: {
                warehouses: [],
                loading: false,
                error: "Failed to load seller warehouses",
              },
            }));
          }
        }),
      );
    };

    fetchSellerWarehouses();
  }, [sellerGroups]);

  const hasDeliveryAddress =
    fulfilmentType === "delivery" &&
    !!selectedAddress &&
    typeof selectedAddress.latitude === "number" &&
    typeof selectedAddress.longitude === "number";

  const sellerCheckoutSummaries = useMemo<SellerCheckoutSummary[]>(() => {
    return sellerGroups.map((group) => {
      const sellerState = group.sellerUserId
        ? warehouseStateBySeller[group.sellerUserId]
        : undefined;

      const warehouses = sellerState?.warehouses || [];
      const sellerWarehousesLoading =
        Boolean(group.sellerUserId) && sellerState?.loading !== false;

      const activeWarehouses = warehouses.filter((w) => w?.isActive !== false);
      const deliveryWarehouses = activeWarehouses.filter(
        (w) => w?.supportsDelivery !== false,
      );
      const pickupWarehouses = activeWarehouses.filter(
        (w) => w?.supportsPickup !== false,
      );

      const selectedPickupId = selectedPickupIds[group.sellerUserId] || null;

      const selectedPickupWarehouse =
        pickupWarehouses.find(
          (warehouse) => warehouse.id === selectedPickupId,
        ) ||
        pickupWarehouses.find((warehouse) => warehouse.isDefaultDispatch) ||
        pickupWarehouses[0] ||
        null;

      const cartDispatchWarehouseIds = Array.from(
        new Set(
          group.items
            .map((item) => String(item?.dispatchWarehouseId || "").trim())
            .filter(Boolean),
        ),
      );

      const cartDispatchWarehouseId =
        cartDispatchWarehouseIds.length === 1
          ? cartDispatchWarehouseIds[0]
          : "";

      const productWarehouse = cartDispatchWarehouseId
        ? deliveryWarehouses.find(
            (warehouse) => warehouse.id === cartDispatchWarehouseId,
          ) || null
        : null;

      const primaryDispatchWarehouse =
        productWarehouse ||
        deliveryWarehouses.find((warehouse) => warehouse.isDefaultDispatch) ||
        deliveryWarehouses[0] ||
        activeWarehouses.find((warehouse) => warehouse.isDefaultDispatch) ||
        activeWarehouses[0] ||
        null;

      const warehouseMessage = getWarehouseMessage(
        group,
        sellerWarehousesLoading,
        warehouses,
        activeWarehouses,
        deliveryWarehouses,
        fulfilmentType,
        pickupWarehouses,
      );

      const deliveryQuote =
        fulfilmentType === "delivery" &&
        hasDeliveryAddress &&
        primaryDispatchWarehouse &&
        selectedAddress
          ? calculateDeliveryQuote(
              group.items,
              {
                lat: primaryDispatchWarehouse.lat,
                lng: primaryDispatchWarehouse.lng,
              },
              {
                lat: selectedAddress.latitude,
                lng: selectedAddress.longitude,
              },
            )
          : null;

      const pricingBreakdown = calculateLocalBreakdown(
        group.items,
        fulfilmentType,
        deliveryQuote,
      );

      const deliveryFeeReady =
        fulfilmentType === "pickup"
          ? !!selectedPickupWarehouse
          : !!deliveryQuote && hasDeliveryAddress && !!primaryDispatchWarehouse;

      return {
        group,
        warehouses,
        activeWarehouses,
        deliveryWarehouses,
        pickupWarehouses,
        selectedPickupWarehouse,
        primaryDispatchWarehouse,
        deliveryQuote,
        pricingBreakdown,
        deliveryFeeReady,
        warehouseMessage,
      };
    });
  }, [
    fulfilmentType,
    hasDeliveryAddress,
    selectedAddress,
    selectedPickupIds,
    sellerGroups,
    warehouseStateBySeller,
  ]);

  useEffect(() => {
    setSelectedPickupIds((prev) => {
      let changed = false;
      const next = { ...prev };

      sellerCheckoutSummaries.forEach((summary) => {
        const sellerId = summary.group.sellerUserId;
        if (!sellerId) return;

        if (!summary.pickupWarehouses.length) {
          if (next[sellerId] !== null) {
            next[sellerId] = null;
            changed = true;
          }
          return;
        }

        const current = next[sellerId];
        const currentStillValid = summary.pickupWarehouses.some(
          (warehouse) => warehouse.id === current,
        );

        if (!current || !currentStillValid) {
          const fallbackId =
            summary.pickupWarehouses.find(
              (warehouse) => warehouse.isDefaultDispatch,
            )?.id ||
            summary.pickupWarehouses[0]?.id ||
            null;

          if (next[sellerId] !== fallbackId) {
            next[sellerId] = fallbackId;
            changed = true;
          }
        }
      });

      return changed ? next : prev;
    });
  }, [sellerCheckoutSummaries]);

  const allSellerGroupsReady = useMemo(() => {
    if (sellerCheckoutSummaries.length === 0) return false;

    return sellerCheckoutSummaries.every((summary) => {
      if (!summary.group.sellerUserId) return false;

      if (fulfilmentType === "delivery") {
        return (
          hasDeliveryAddress &&
          !!summary.primaryDispatchWarehouse &&
          summary.deliveryFeeReady
        );
      }

      return !!summary.selectedPickupWarehouse && summary.deliveryFeeReady;
    });
  }, [fulfilmentType, hasDeliveryAddress, sellerCheckoutSummaries]);

  const pricingBreakdown = useMemo<PricingBreakdown>(() => {
    const totals = sellerCheckoutSummaries.reduce(
      (acc, summary) => {
        acc.productSubtotal += Number(
          summary.pricingBreakdown.productSubtotal || 0,
        );
        acc.serviceFee += Number(summary.pricingBreakdown.serviceFee || 0);
        acc.deliveryFee += Number(summary.pricingBreakdown.deliveryFee || 0);
        acc.discountAmount += Number(
          summary.pricingBreakdown.discountAmount || 0,
        );
        acc.deliveryDistanceKm += Number(
          summary.pricingBreakdown.deliveryDistanceKm || 0,
        );
        acc.billableWeightKg += Number(
          summary.pricingBreakdown.billableWeightKg || 0,
        );
        acc.bulkySurcharge += Number(
          summary.pricingBreakdown.bulkySurcharge || 0,
        );
        acc.total += Number(summary.pricingBreakdown.total || 0);
        return acc;
      },
      {
        productSubtotal: 0,
        serviceFee: 0,
        deliveryFee: 0,
        discountAmount: 0,
        deliveryDistanceKm: 0,
        billableWeightKg: 0,
        bulkySurcharge: 0,
        total: 0,
      },
    );

    return {
      currency: "MWK",
      productSubtotal: roundMoney(totals.productSubtotal),
      serviceFee: roundMoney(totals.serviceFee),
      deliveryFee: roundMoney(totals.deliveryFee),
      discountAmount: roundMoney(totals.discountAmount),
      deliveryDistanceKm: roundMoney(totals.deliveryDistanceKm),
      billableWeightKg: roundMoney(totals.billableWeightKg),
      bulkySurcharge: roundMoney(totals.bulkySurcharge),
      total: roundMoney(totals.total),
    };
  }, [sellerCheckoutSummaries]);

  const deliveryFeePackageCount = useMemo(() => {
    if (fulfilmentType !== "delivery") return 0;

    return sellerCheckoutSummaries.filter((summary) => {
      return Number(summary.pricingBreakdown.deliveryFee || 0) > 0;
    }).length;
  }, [fulfilmentType, sellerCheckoutSummaries]);

  const deliveryRouteSummary = useMemo(() => {
    if (fulfilmentType !== "delivery" || !selectedAddress) return null;

    return (
      sellerCheckoutSummaries.find(
        (summary) =>
          !!summary.primaryDispatchWarehouse && !!summary.deliveryQuote,
      ) ||
      sellerCheckoutSummaries.find(
        (summary) => !!summary.primaryDispatchWarehouse,
      ) ||
      null
    );
  }, [fulfilmentType, selectedAddress, sellerCheckoutSummaries]);

  const routeWarehouse = deliveryRouteSummary?.primaryDispatchWarehouse || null;

  useEffect(() => {
    let cancelled = false;

    const clearRoute = () => {
      if (cancelled) {
        return;
      }

      setDirections(null);
      setDistance("");
      setDuration("");
    };

    const hasValidRouteInput =
      fulfilmentType === "delivery" &&
      !!routeWarehouse &&
      !!selectedAddress &&
      typeof selectedAddress.latitude === "number" &&
      Number.isFinite(selectedAddress.latitude) &&
      typeof selectedAddress.longitude === "number" &&
      Number.isFinite(selectedAddress.longitude);

    if (!hasValidRouteInput) {
      clearRoute();

      return () => {
        cancelled = true;
      };
    }

    if (googleMapsLoadError) {
      console.error(
        "Google Maps failed to load before directions could be calculated:",
        googleMapsLoadError,
      );

      clearRoute();

      return () => {
        cancelled = true;
      };
    }

    if (
      !isGoogleMapsLoaded ||
      typeof window === "undefined" ||
      !window.google?.maps
    ) {
      /*
       * The Maps script is still loading. Do not construct
       * DirectionsService yet. This effect runs again when isLoaded changes.
       */
      return () => {
        cancelled = true;
      };
    }

    const loadDirections = async () => {
      try {
        let DirectionsServiceConstructor = window.google.maps.DirectionsService;

        /*
         * google.maps may exist before the routes library is ready.
         * Explicitly import the routes library when importLibrary is
         * available, then construct DirectionsService from that result.
         */
        if (typeof window.google.maps.importLibrary === "function") {
          const routesLibrary = (await window.google.maps.importLibrary(
            "routes",
          )) as unknown as {
            DirectionsService: typeof google.maps.DirectionsService;
          };

          DirectionsServiceConstructor = routesLibrary.DirectionsService;
        }

        if (cancelled || typeof DirectionsServiceConstructor !== "function") {
          if (!cancelled) {
            throw new Error("Google Maps DirectionsService is not available.");
          }

          return;
        }

        const directionsService = new DirectionsServiceConstructor();

        directionsService.route(
          {
            origin: {
              lat: routeWarehouse.lat,
              lng: routeWarehouse.lng,
            },
            destination: {
              lat: selectedAddress.latitude,
              lng: selectedAddress.longitude,
            },
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (cancelled) {
              return;
            }

            if (status === "OK" && result) {
              setDirections(result);

              const leg = result.routes[0]?.legs[0];

              setDistance(leg?.distance?.text || "");
              setDuration(leg?.duration?.text || "");

              if (leg?.start_location) {
                setCenter({
                  lat: leg.start_location.lat(),
                  lng: leg.start_location.lng(),
                });
              }

              return;
            }

            console.error("Delivery directions fetch failed:", status);

            clearRoute();

            toast({
              title: "Failed to load route",
              description:
                "Map directions could not be loaded for this delivery.",
              status: "warning",
              duration: 4000,
              isClosable: true,
            });
          },
        );
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }

        console.error("Delivery directions initialization failed:", error);

        clearRoute();

        toast({
          title: "Failed to load route",
          description:
            "Map directions could not be initialized. Please try again.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      }
    };

    void loadDirections();

    return () => {
      cancelled = true;
    };
  }, [
    fulfilmentType,
    routeWarehouse,
    selectedAddress,
    isGoogleMapsLoaded,
    googleMapsLoadError,
    toast,
  ]);

  const customerName = customerNameInput.trim();

  const inputCustomerPhone = useMemo(
    () => buildFullPhone(countryCode, phoneNumberInput),
    [countryCode, phoneNumberInput],
  );

  const phoneValidationMessage = useMemo(
    () => getLocalPhoneValidationMessage(countryCode, phoneNumberInput),
    [countryCode, phoneNumberInput],
  );

  const isCurrentPhoneValid = !phoneValidationMessage;

  const hasValidCustomerName = customerName.length >= 2;

  const shouldShowNameValidation = customerNameTouched && !hasValidCustomerName;

  const shouldShowPhoneValidation =
    Boolean(phoneValidationMessage) &&
    (phoneNumberTouched || Boolean(invalidSavedCustomerPhone));

  const isCustomerDetailsReady =
    !profileLoading && hasValidCustomerName && isCurrentPhoneValid;

  const customerDetailsPendingMessage = profileLoading
    ? "Checking profile details"
    : !hasValidCustomerName
      ? "Customer name required"
      : !isCurrentPhoneValid
        ? "Valid WhatsApp number required"
        : "Customer details required";

  const customerPhoneDisplayValue = profileLoading
    ? "Checking profile..."
    : inputCustomerPhone || savedCustomerPhone || "Missing";

  const payableAmount =
    fulfilmentType === "delivery" && !allSellerGroupsReady
      ? 0
      : Number(pricingBreakdown.total || cart.total || 0);

  const deliveryPendingMessage = !hasDeliveryAddress
    ? "Delivery address not set"
    : !allSellerGroupsReady
      ? "Some seller warehouses are not ready"
      : "Calculating delivery fee";

  const isGuestAddressSelectionReady =
    Boolean(guestAddressLine.trim()) &&
    !!guestAddressCoords &&
    Number.isFinite(guestAddressCoords.lat) &&
    Number.isFinite(guestAddressCoords.lng);

  useEffect(() => {
    if (!user?.uid && guestAddressTouched && isGuestAddressSelectionReady) {
      setGuestAddressTouched(false);
    }
  }, [guestAddressTouched, isGuestAddressSelectionReady, user?.uid]);

  const handleGuestAddressInputChanged = (value: string) => {
    /*
     * Keep the visible search text, but remove its coordinates and any
     * old confirmation. Typed text alone can never become the order
     * address. A previously confirmed address remains editable.
     */
    if (hasConfirmedGuestAddress || Boolean(selectedAddress)) {
      setGuestAddressEditingExisting(true);
    }

    setGuestAddressLine(value);
    setGuestAddressCoords(null);
    setSelectedAddress(null);
    setGuestAddressTouched(false);
  };

  const handleGuestAddressSelected = (
    addressLine: string,
    coords: { lat: number; lng: number },
  ) => {
    const completeSelectedAddress = String(addressLine || "").trim();
    const selectedCoords = {
      lat: Number(coords?.lat),
      lng: Number(coords?.lng),
    };

    if (
      !completeSelectedAddress ||
      !Number.isFinite(selectedCoords.lat) ||
      !Number.isFinite(selectedCoords.lng)
    ) {
      setGuestAddressLine("");
      setGuestAddressCoords(null);
      setGuestAddressTouched(true);
      return;
    }

    /*
     * Stage the complete Google address and coordinates together. The order
     * does not use the new location until Confirm delivery address is pressed.
     */
    if (hasConfirmedGuestAddress) {
      setGuestAddressEditingExisting(true);
    }

    setSelectedAddress(null);
    setGuestAddressLine(completeSelectedAddress);
    setGuestAddressCoords(selectedCoords);
    setGuestAddressTouched(false);
  };

  const useGuestAddressForOrder = () => {
    setGuestAddressTouched(true);

    const resolvedLabel = guestAddressLabel.trim() || "Delivery address";
    const resolvedAddressLine = guestAddressLine.trim();

    if (
      !isGuestAddressSelectionReady ||
      !resolvedAddressLine ||
      !guestAddressCoords
    ) {
      toast({
        title: "Delivery address required",
        description:
          "Search for the delivery address and select it from the suggestions.",
        status: "warning",
        duration: 4500,
        isClosable: true,
      });
      return;
    }

    const wasEditingExistingAddress =
      hasConfirmedGuestAddress || guestAddressEditingExisting;

    const guestAddress: UserAddress = {
      id:
        selectedAddress?.id ||
        readGuestCheckoutDetails()?.address?.id ||
        `guest-address-${Date.now()}`,
      label: resolvedLabel,
      addressLine: resolvedAddressLine,
      latitude: guestAddressCoords.lat,
      longitude: guestAddressCoords.lng,
      isCurrent: true,
      isPickup: false,
      createdAt:
        selectedAddress?.createdAt ||
        readGuestCheckoutDetails()?.address?.createdAt ||
        Date.now(),
    };

    const fullPhone =
      buildFullPhone(countryCode, phoneNumberInput) || savedCustomerPhone || "";

    saveGuestCheckoutDetails({
      name: customerNameInput.trim(),
      phoneNumber: fullPhone,
      address: {
        id: guestAddress.id,
        label: guestAddress.label,
        addressLine: guestAddress.addressLine,
        latitude: guestAddress.latitude,
        longitude: guestAddress.longitude,
        isCurrent: true,
        isPickup: false,
        createdAt: guestAddress.createdAt || Date.now(),
      },
    });

    setSelectedAddress(guestAddress);
    setShowAddAddress(false);
    setGuestAddressTouched(false);
    setHasConfirmedGuestAddress(true);
    setGuestAddressEditingExisting(false);

    toast({
      title: wasEditingExistingAddress
        ? "Delivery address updated"
        : "Delivery address saved",
      description:
        "Your guest name, WhatsApp number, and delivery address are saved together on this device.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const openAndFocusAddressForm = () => {
    setFulfilmentType("delivery");
    setShowCheckoutDetails(true);

    if (!user?.uid) {
      const existingAddressLine = String(
        selectedAddress?.addressLine || "",
      ).trim();

      const existingLatitude = Number(selectedAddress?.latitude);
      const existingLongitude = Number(selectedAddress?.longitude);
      const hasExistingCoordinates =
        Number.isFinite(existingLatitude) && Number.isFinite(existingLongitude);

      setGuestAddressLabel(
        String(selectedAddress?.label || "Delivery address"),
      );
      setGuestAddressLine(existingAddressLine);
      setGuestAddressCoords(
        existingAddressLine && hasExistingCoordinates
          ? {
              lat: existingLatitude,
              lng: existingLongitude,
            }
          : null,
      );
      setGuestAddressTouched(false);
      setHasConfirmedGuestAddress(
        Boolean(existingAddressLine && hasExistingCoordinates),
      );
      setGuestAddressEditingExisting(false);

      /*
       * Force a fresh Google autocomplete input for both add and change.
       * When changing, the complete currently selected address is shown.
       */
      setGuestAddressInputKey((currentKey) => currentKey + 1);
    }

    setShowAddAddress(true);
    setHighlightAddAddressForm(true);

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        addAddressFormRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 180);
    });

    window.setTimeout(() => {
      setHighlightAddAddressForm(false);
    }, 2600);
  };

  const openAndFocusCustomerDetails = () => {
    setShowCheckoutDetails(true);
    setHighlightCustomerDetails(true);
    setCustomerNameTouched(true);
    setPhoneNumberTouched(true);

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        customerDetailsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 180);
    });

    window.setTimeout(() => {
      setHighlightCustomerDetails(false);
    }, 2600);
  };

  const handleCustomerNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCustomerNameInput(event.target.value);
    setCustomerNameTouched(true);
    setCustomerDetailsSaveSuccess(false);
  };

  const handleCountryChange = (value: SupportedPhoneCountryCode) => {
    setCountryCode(value);
    setPhoneNumberInput("");
    setSavedCustomerPhone("");
    setInvalidSavedCustomerPhone("");
    setPhoneNumberTouched(true);
    setCustomerDetailsSaveSuccess(false);
  };

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumberInput(value);
    setSavedCustomerPhone("");
    setInvalidSavedCustomerPhone("");
    setPhoneNumberTouched(true);
    setCustomerDetailsSaveSuccess(false);
  };

  const saveCustomerDetailsIfNeeded = async (
    showSuccessToast = false,
  ): Promise<ResolvedCustomerDetails | null> => {
    const resolvedName = customerNameInput.trim();
    const fullPhone = buildFullPhone(countryCode, phoneNumberInput);

    setCustomerNameTouched(true);
    setPhoneNumberTouched(true);

    if (resolvedName.length < 2) {
      toast({
        title: "Customer name required",
        description: "Please enter the customer name before checkout.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });

      openAndFocusCustomerDetails();
      return null;
    }

    if (savedCustomerPhone && !isValidWhatsAppFullPhone(savedCustomerPhone)) {
      setInvalidSavedCustomerPhone(savedCustomerPhone);
      setSavedCustomerPhone("");
    }

    if (!isValidLocalPhoneInput(countryCode, phoneNumberInput)) {
      toast({
        title: invalidSavedCustomerPhone
          ? "Saved WhatsApp number is invalid"
          : "Valid WhatsApp number required",
        description:
          countryCode === "+27"
            ? "Please enter the full local WhatsApp number, for example 647367388."
            : "Please enter the full local WhatsApp number.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });

      openAndFocusCustomerDetails();
      return null;
    }

    if (!isValidWhatsAppFullPhone(fullPhone)) {
      toast({
        title: "Invalid WhatsApp number",
        description:
          "Please check the country code and enter the full WhatsApp number.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });

      openAndFocusCustomerDetails();
      return null;
    }

    if (!user?.uid) {
      setSavedCustomerPhone(fullPhone);
      setInvalidSavedCustomerPhone("");
      setCustomerDetailsSaveSuccess(true);
      setPhoneNumberTouched(false);
      setCustomerNameTouched(false);

      saveGuestCheckoutDetails({
        name: resolvedName,
        phoneNumber: fullPhone,
      });

      if (showSuccessToast) {
        toast({
          title: "Customer details saved",
          status: "success",
          duration: 2500,
          isClosable: true,
        });
      }

      return {
        name: resolvedName,
        phoneNumber: fullPhone,
      };
    }

    const profilePhoneNumbers = [
      savedCustomerPhone,
      invalidSavedCustomerPhone,
      customerProfile?.whatsappNumber,
      customerProfile?.phoneNumber,
      customerProfile?.phone,
      customerProfile?.businessPhoneNumber,
      customerProfile?.businessPhone,
      (userSettings as any)?.whatsappNumber,
      (userSettings as any)?.phoneNumber,
      (userSettings as any)?.phone,
      (userSettings as any)?.businessPhoneNumber,
      (userSettings as any)?.businessPhone,
      user?.phoneNumber,
      user?.providerData?.[0]?.phoneNumber,
    ]
      .map(normalizeStoredWhatsAppPhone)
      .filter(Boolean);

    const phoneAlreadyBelongsToCurrentUser =
      profilePhoneNumbers.includes(fullPhone);

    setSavingCustomerDetails(true);

    try {
      if (!phoneAlreadyBelongsToCurrentUser) {
        await claimPhoneNumberViaBackend({
          currentUid: user.uid,
          phoneNumber: fullPhone,
        });
      }

      await updateDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        firebaseUid: user.uid,
        displayName: resolvedName,
        name: resolvedName,
        phone: fullPhone,
        phoneNumber: fullPhone,
        whatsappNumber: fullPhone,
      });

      setSavedCustomerPhone(fullPhone);
      setInvalidSavedCustomerPhone("");
      setCustomerDetailsSaveSuccess(true);
      setPhoneNumberTouched(false);
      setCustomerNameTouched(false);
      setCustomerProfile((prev) => ({
        ...(prev || {}),
        uid: user.uid,
        firebaseUid: user.uid,
        displayName: resolvedName,
        name: resolvedName,
        phone: fullPhone,
        phoneNumber: fullPhone,
        whatsappNumber: fullPhone,
      }));

      if (showSuccessToast) {
        toast({
          title: "Customer details saved",
          description: "Your profile details were updated.",
          status: "success",
          duration: 2500,
          isClosable: true,
        });
      }

      return {
        name: resolvedName,
        phoneNumber: fullPhone,
      };
    } catch (error) {
      const err = error as ClaimPhoneConflictError;

      console.error("Failed to save customer details:", err);

      const errorMessage = String(err.message || "").toLowerCase();
      const isConflict =
        err.code === "PHONE_ALREADY_IN_USE" ||
        err.status === 409 ||
        errorMessage.includes("already linked") ||
        errorMessage.includes("already in use");

      toast({
        title: isConflict
          ? "Phone number already linked"
          : "Could not save customer details",
        description: isConflict
          ? "This WhatsApp number is already linked to another account."
          : err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      openAndFocusCustomerDetails();
      return null;
    } finally {
      setSavingCustomerDetails(false);
    }
  };

  const handleSaveCustomerDetails = async () => {
    await saveCustomerDetailsIfNeeded(true);
  };

  const handleCustomerDetailsKeyDown = async (
    event: KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    await handleSaveCustomerDetails();
  };

  const createOrder = async (chargeId?: string): Promise<string | null> => {
    try {
      if (warehousesLoading) {
        toast({
          title: "Loading warehouses",
          description:
            "Please wait until all seller warehouse information has loaded.",
          status: "info",
          duration: 3500,
          isClosable: true,
        });
        return null;
      }

      if (sellerCheckoutSummaries.length === 0) {
        toast({
          title: "Cart is empty",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return null;
      }

      if (!allSellerGroupsReady) {
        const firstBlocked = sellerCheckoutSummaries.find(
          (summary) => !summary.deliveryFeeReady || !summary.group.sellerUserId,
        );

        toast({
          title: "Checkout not ready",
          description:
            firstBlocked?.warehouseMessage ||
            "Please resolve seller warehouses and delivery details before paying.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });

        return null;
      }

      if (fulfilmentType === "delivery" && !selectedAddress) {
        toast({ title: "Delivery address required", status: "warning" });
        return null;
      }

      const resolvedCustomerDetails = await saveCustomerDetailsIfNeeded(false);

      if (!resolvedCustomerDetails) {
        return null;
      }

      const sellerOrders: SellerGroupedOrderPayload[] =
        sellerCheckoutSummaries.map((summary) => {
          const resolvedWarehouse =
            fulfilmentType === "pickup"
              ? summary.selectedPickupWarehouse
              : summary.primaryDispatchWarehouse;

          if (!resolvedWarehouse) {
            throw new Error(
              summary.warehouseMessage ||
                `Could not resolve warehouse for ${summary.group.sellerName}.`,
            );
          }

          const normalizedItems = summary.group.items.map((item: any) => ({
            _id: item._id!,
            productId: item.productId || item._id || "",
            userId: item.userId || summary.group.sellerUserId,
            sellerUserId: summary.group.sellerUserId,
            ownerId: item.ownerId || item.userId || summary.group.sellerUserId,
            dispatchWarehouseId:
              item.dispatchWarehouseId || resolvedWarehouse.id || "",
            sellerBusinessName:
              item.sellerBusinessName || summary.group.sellerName || "",
            title: item.title,
            description: item.description ?? "",
            category: item.category ?? "Car Parts",
            imageUrl:
              item.selectedImage ||
              item.imageUrl ||
              item.image ||
              (Array.isArray(item.images) ? item.images[0] : "") ||
              "",
            price: Number(item.selectedPrice ?? item.price ?? 0),
            quantity: Number(item.quantity || 1),
            weightKg: Number(item.weightKg ?? 0),
            lengthCm: Number(item.lengthCm ?? 0),
            widthCm: Number(item.widthCm ?? 0),
            heightCm: Number(item.heightCm ?? 0),
            bulky: Boolean(item.bulky),
            isPhysical:
              typeof item.isPhysical === "boolean" ? item.isPhysical : true,
            ...(Array.isArray(item.selectedVariants) &&
            item.selectedVariants.length > 0
              ? {
                  selectedVariants: item.selectedVariants,
                  variantSummary: getVariantSummary(item),
                }
              : {}),
          }));

          return {
            sellerUserId: summary.group.sellerUserId,
            ownerUserId: summary.group.sellerUserId,
            sellerName: summary.group.sellerName,

            items: normalizedItems,

            total: Number(summary.pricingBreakdown.total || 0),
            currency: "MWK",

            fromAddress: resolvedWarehouse.address,
            toAddress:
              fulfilmentType === "delivery" && selectedAddress
                ? selectedAddress.addressLine
                : resolvedWarehouse.address,

            fromLocation: {
              lat: resolvedWarehouse.lat,
              lng: resolvedWarehouse.lng,
            },

            toLocation:
              fulfilmentType === "delivery" && selectedAddress
                ? {
                    lat: selectedAddress.latitude,
                    lng: selectedAddress.longitude,
                  }
                : {
                    lat: resolvedWarehouse.lat,
                    lng: resolvedWarehouse.lng,
                  },

            deliveryInstructions:
              fulfilmentType === "delivery"
                ? ""
                : `Pickup at ${resolvedWarehouse.name}`,

            pickupAddress: resolvedWarehouse.address,
            pickupWarehouseId: resolvedWarehouse.id,
            pickupWarehouseName: resolvedWarehouse.name,
            pickupWarehouseAddress: resolvedWarehouse.address,
            dispatchWarehouseId: resolvedWarehouse.id,

            checkoutMode: fulfilmentType,
            category: "product",

            deliveryFee: Number(summary.pricingBreakdown.deliveryFee || 0),
            distanceKm: Number(summary.deliveryQuote?.distanceKm || 0),
            discountAmount: Number(
              summary.pricingBreakdown.discountAmount || 0,
            ),

            pricingBreakdown: summary.pricingBreakdown,
          };
        });

      const payload: CreateGroupedCheckoutPayload = {
        userId: user?.uid ?? "guest",
        email: customerProfile?.email ?? user?.email ?? "",
        phoneNumber: resolvedCustomerDetails.phoneNumber,
        name: resolvedCustomerDetails.name,
        checkoutMode: fulfilmentType,
        chargeId: chargeId || "",
        payment: {
          method: "",
          provider: "",
          status: "pending",
        },
        sellerOrders,
      };

      const result = await createGroupedCheckoutOrder(payload).unwrap();

      toast({
        title:
          result.orderCount === 1
            ? "Order created"
            : `${result.orderCount} seller orders created`,
        description:
          result.orderCount === 1
            ? "A waybill has been created for dispatch."
            : "Your checkout has been split into seller fulfilment orders and waybills.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      return result.primaryOrderId || result.orders?.[0]?._id || null;
    } catch (err: any) {
      console.error("CREATE GROUPED CHECKOUT ERROR:", err);

      toast({
        title: "Order creation failed",
        description:
          err?.data?.message ||
          err?.message ||
          "Please check seller warehouses and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      return null;
    }
  };

  const showCustomerDetailsActionPrompt = !isCustomerDetailsReady;

  const showDeliveryActionPrompt = !warehousesLoading && !allSellerGroupsReady;

  const paymentActionDisabled =
    warehousesLoading ||
    !allSellerGroupsReady ||
    !isCustomerDetailsReady ||
    Number(payableAmount || 0) <= 0;

  return (
    <Flex
      direction={{ base: "column", xl: "row" }}
      gap={6}
      color={pageText}
      w="100%"
      maxW="100%"
      minW={0}
      overflowX="hidden"
    >
      <Box
        flex={1}
        minW={0}
        w="100%"
        maxW="100%"
        bg={cardBg}
        p={5}
        borderRadius={cardsBorderRadii}
        boxShadow={panelShadow}
        border={`1px solid ${borderColor}`}
        backdropFilter={isDark ? "blur(8px)" : undefined}
      >
        <Heading size="lg" mb={4} color={pageText}>
          Checkout
        </Heading>

        <Button
          w="full"
          mb={4}
          onClick={() => setShowCheckoutDetails((prev) => !prev)}
          bg={softBg}
          color={pageText}
          border={`1px solid ${borderColor}`}
          borderRadius={cardsBorderRadii}
          _hover={{ bg: softBg, borderColor: borderStrong }}
          _active={{ bg: softBg }}
          px={4}
          py={6}
          h="auto"
        >
          <Flex w="full" align="center" justify="space-between" gap={3}>
            <Text fontWeight="700" textAlign="left">
              {showCheckoutDetails
                ? "Hide checkout details"
                : "Show checkout details"}
            </Text>

            {showCheckoutDetails ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronDown size={18} />
            )}
          </Flex>
        </Button>

        <Collapse in={showCheckoutDetails} animateOpacity>
          <Box
            borderRadius={cardsBorderRadii}
            overflow="hidden"
            border={`1px solid ${borderColor}`}
            bg={softBg}
          >
            <Box overflowX="hidden" overflowY="hidden" w="100%" maxW="100%">
              <Table
                variant="simple"
                size={{ base: "xs", md: "sm" }}
                w="100%"
                maxW="100%"
                sx={{
                  tableLayout: "fixed",
                }}
              >
                <Thead bg={theme.hover}>
                  <Tr h="35px">
                    <Th
                      color={theme.textSubtle}
                      fontSize={{ base: "xs", md: "sm" }}
                      px={{ base: 2, md: 4 }}
                      py={0}
                    >
                      Product
                    </Th>
                    <Th
                      color={theme.textSubtle}
                      fontSize={{ base: "xs", md: "sm" }}
                      px={{ base: 2, md: 4 }}
                      py={0}
                    >
                      Price
                    </Th>
                    <Th
                      color={theme.textSubtle}
                      fontSize={{ base: "xs", md: "sm" }}
                      px={{ base: 2, md: 4 }}
                      py={0}
                    >
                      Qty
                    </Th>
                    <Th
                      display={{ base: "none", sm: "table-cell" }}
                      color={theme.textSubtle}
                      fontSize={{ base: "xs", md: "sm" }}
                      px={{ base: 2, md: 4 }}
                      py={0}
                    >
                      Total
                    </Th>
                    <Th
                      color={theme.textSubtle}
                      fontSize={{ base: "xs", md: "sm" }}
                      px={{ base: 2, md: 4 }}
                      py={0}
                    >
                      Actions
                    </Th>
                  </Tr>
                </Thead>

                <Tbody bg={isDark ? "transparent" : "white"}>
                  {cart.items.map((item: any, index: number) => {
                    const itemId = item._id || item.id || item.productId;

                    const itemPrice = Number(
                      item.selectedPrice ?? item.price ?? 0,
                    );

                    return (
                      <Tr
                        key={itemId}
                        bg={index % 2 === 0 ? "transparent" : theme.hover}
                        transition="background 0.2s ease"
                        _hover={{
                          bg: theme.selected || theme.hover,
                        }}
                        sx={{
                          "& > td": {
                            py: {
                              base: "calc(0.25rem + 3px)",
                              md: "calc(0.5rem + 3px)",
                            },
                          },
                        }}
                      >
                        <Td px={{ base: 2, md: 4 }} borderColor={theme.divider}>
                          <Flex align="center" gap={2}>
                            <Image
                              src={
                                item.imageUrl ||
                                item.selectedImage ||
                                item.image ||
                                (Array.isArray(item.images)
                                  ? item.images[0]
                                  : "")
                              }
                              boxSize={{ base: "30px", md: "50px" }}
                              borderRadius={cardsBorderRadii}
                              border="1px solid"
                              borderColor={theme.border}
                              objectFit="cover"
                              flexShrink={0}
                            />

                            <Text
                              fontSize={{ base: "xs", md: "sm" }}
                              color={theme.text}
                              maxW={{ base: "72px", sm: "120px", md: "240px" }}
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                              title={item.title}
                            >
                              {item.title}
                            </Text>
                          </Flex>
                        </Td>

                        <Td
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          px={{ base: 2, md: 4 }}
                          borderColor={theme.divider}
                          color={theme.textMuted}
                        >
                          MWK{itemPrice.toFixed(2)}
                        </Td>

                        <Td px={{ base: 2, md: 4 }} borderColor={theme.divider}>
                          <Flex align="center" gap={2}>
                            <Button
                              size={{ base: "sm", md: "xs" }}
                              minW={{ base: "24px", md: "24px" }}
                              h={{ base: "28px", md: "24px" }}
                              fontSize={{ base: "md", md: "xs" }}
                              borderRadius={cardsBorderRadii}
                              variant="outline"
                              borderColor={theme.borderStrong}
                              color={theme.text}
                              _hover={{ bg: theme.hover }}
                              onClick={() => dispatch(decreaseQuantity(itemId))}
                            >
                              -
                            </Button>

                            <Text
                              minW="20px"
                              textAlign="center"
                              fontSize={{ base: "sm", md: "sm" }}
                              color={theme.text}
                            >
                              {item.quantity}
                            </Text>

                            <Button
                              size={{ base: "sm", md: "xs" }}
                              minW={{ base: "24px", md: "24px" }}
                              h={{ base: "28px", md: "24px" }}
                              fontSize={{ base: "md", md: "xs" }}
                              borderRadius={cardsBorderRadii}
                              variant="outline"
                              borderColor={theme.borderStrong}
                              color={theme.text}
                              _hover={{ bg: theme.hover }}
                              onClick={() => dispatch(increaseQuantity(itemId))}
                            >
                              +
                            </Button>
                          </Flex>
                        </Td>

                        <Td
                          display={{ base: "none", sm: "table-cell" }}
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          px={{ base: 2, md: 4 }}
                          borderColor={theme.divider}
                          color={theme.text}
                          fontWeight="600"
                        >
                          MWK
                          {(itemPrice * Number(item.quantity || 0)).toFixed(2)}
                        </Td>

                        <Td px={{ base: 2, md: 4 }} borderColor={theme.divider}>
                          <Flex justify="center">
                            <Trash
                              size={16}
                              cursor="pointer"
                              color="#ff6b6b"
                              onClick={() => dispatch(removeFromCart(itemId))}
                            />
                          </Flex>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {sellerGroups.length > 1 && (
            <Box
              mt={4}
              p={4}
              borderRadius={cardsBorderRadii}
              bg={softBg}
              border={`1px solid ${borderColor}`}
            >
              <Badge
                colorScheme="teal"
                mb={2}
                px={3}
                py={1}
                borderRadius={cardsBorderRadii}
              >
                Multi-seller checkout
              </Badge>
              <Text fontWeight="700" color={pageText}>
                Your cart will be split into {sellerGroups.length} seller
                orders.
              </Text>
              <Text mt={1} fontSize="sm" color={mutedText}>
                Delivery is shown as one total at checkout. Each seller order
                keeps its own delivery fee in the background.
              </Text>
            </Box>
          )}

          <Box
            ref={customerDetailsRef}
            mt={4}
            p={4}
            borderRadius={cardsBorderRadii}
            bg={softBg}
            border="1px solid"
            borderColor={
              highlightCustomerDetails
                ? theme.brand?.electricBlue || theme.primary
                : !isCustomerDetailsReady
                  ? "orange.300"
                  : borderColor
            }
            boxShadow={
              highlightCustomerDetails
                ? `0 0 0 4px ${isDark ? "rgba(110,197,196,0.20)" : "rgba(49,151,149,0.18)"}`
                : undefined
            }
            transition="all 0.25s ease"
          >
            <Flex
              justify="space-between"
              align={{ base: "start", md: "center" }}
              direction={{ base: "column", md: "row" }}
              gap={3}
              mb={4}
            >
              <Box>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge
                    colorScheme={isCustomerDetailsReady ? "green" : "orange"}
                    borderRadius={cardsBorderRadii}
                    px={3}
                    py={1}
                  >
                    Customer Details
                  </Badge>

                  {profileLoading && (
                    <Badge colorScheme="gray" borderRadius={cardsBorderRadii}>
                      Checking profile
                    </Badge>
                  )}
                </HStack>

                <Text mt={2} fontWeight="700" color={pageText}>
                  {user?.uid
                    ? "Confirm or edit your profile details"
                    : "Add customer details for checkout"}
                </Text>

                <Text mt={1} fontSize="sm" color={mutedText}>
                  {user?.uid
                    ? "These details are saved to your user profile and used for order updates."
                    : "These details are saved locally for guest checkout and used for order updates."}
                </Text>
              </Box>

              <Button
                size="sm"
                w={{ base: "100%", md: "auto" }}
                onClick={handleSaveCustomerDetails}
                isLoading={savingCustomerDetails}
                isDisabled={savingCustomerDetails || profileLoading}
                color={theme.textOnPrimary}
                bgImage={theme.gradients.primaryButton}
                _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                boxShadow={theme.shadowSm}
                flexShrink={0}
              >
                ✓ Save details
              </Button>
            </Flex>

            <VStack
              align="stretch"
              spacing={4}
              onKeyDown={handleCustomerDetailsKeyDown}
            >
              <Box>
                <Text mb={2} fontSize="sm" fontWeight="700" color={mutedText}>
                  Name
                </Text>

                <Input
                  value={customerNameInput}
                  onChange={handleCustomerNameChange}
                  placeholder="Enter customer name"
                  bg={cardBg}
                  color={pageText}
                  borderColor={
                    shouldShowNameValidation ? "red.400" : borderColor
                  }
                  _placeholder={{ color: mutedText }}
                  _hover={{
                    borderColor: shouldShowNameValidation
                      ? "red.400"
                      : borderStrong,
                  }}
                  _focus={{
                    borderColor: shouldShowNameValidation
                      ? "red.400"
                      : borderStrong,
                    boxShadow: shouldShowNameValidation
                      ? "0 0 0 1px red"
                      : `0 0 0 1px ${borderStrong}`,
                  }}
                />

                {shouldShowNameValidation && (
                  <Text mt={2} fontSize="sm" color="red.500">
                    Customer name is required.
                  </Text>
                )}
              </Box>

              <Box>
                <WhatsAppPhoneInput
                  isRequired
                  label="WhatsApp number"
                  countryCode={countryCode}
                  phoneNumber={phoneNumberInput}
                  onCountryCodeChange={handleCountryChange}
                  onPhoneNumberChange={handlePhoneNumberChange}
                  labelColor={mutedText}
                  inputStyles={{
                    bg: cardBg,
                    color: pageText,
                    borderColor:
                      shouldShowPhoneValidation || invalidSavedCustomerPhone
                        ? "red.400"
                        : customerDetailsSaveSuccess
                          ? "green.400"
                          : borderColor,
                    _placeholder: {
                      color: mutedText,
                    },
                    _hover: {
                      borderColor:
                        shouldShowPhoneValidation || invalidSavedCustomerPhone
                          ? "red.400"
                          : borderStrong,
                    },
                    _focus: {
                      borderColor:
                        shouldShowPhoneValidation || invalidSavedCustomerPhone
                          ? "red.400"
                          : borderStrong,
                      boxShadow:
                        shouldShowPhoneValidation || invalidSavedCustomerPhone
                          ? "0 0 0 1px red"
                          : `0 0 0 1px ${borderStrong}`,
                    },
                  }}
                />

                {shouldShowPhoneValidation && (
                  <Text mt={2} fontSize="sm" color="red.500">
                    {phoneValidationMessage}
                  </Text>
                )}

                {!!invalidSavedCustomerPhone && !shouldShowPhoneValidation && (
                  <Text mt={2} fontSize="sm" color="red.500">
                    Saved number {invalidSavedCustomerPhone} is invalid. Please
                    save a valid WhatsApp number.
                  </Text>
                )}
              </Box>

              {customerDetailsSaveSuccess && isCustomerDetailsReady && (
                <Text fontSize="sm" color="green.500">
                  Customer details saved. Notifications will be sent to{" "}
                  <b>{customerPhoneDisplayValue}</b>.
                </Text>
              )}

              {!customerDetailsSaveSuccess &&
                isCustomerDetailsReady &&
                (customerNameTouched || phoneNumberTouched) && (
                  <Text fontSize="sm" color={mutedText}>
                    Press <b>Enter</b> or tap <b>✓ Save details</b> to update
                    these details before payment.
                  </Text>
                )}
            </VStack>
          </Box>

          <Divider my={5} borderColor={dividerColor} />

          <OrderPricingBreakdownCard
            breakdown={pricingBreakdown}
            fallbackTotal={payableAmount}
            title="Payment Breakdown"
            fulfilmentType={fulfilmentType}
            deliveryFeeReady={allSellerGroupsReady}
            pendingDeliveryMessage={deliveryPendingMessage}
          />

          {fulfilmentType === "delivery" &&
            allSellerGroupsReady &&
            deliveryFeePackageCount > 1 && (
              <Box
                mt={3}
                p={3}
                borderRadius={cardsBorderRadii}
                bg={softBg}
                border={`1px solid ${borderColor}`}
              >
                <Text color={mutedText} fontSize="sm">
                  Delivery is combined into one checkout fee. It covers{" "}
                  {deliveryFeePackageCount} seller dispatch packages.
                </Text>
              </Box>
            )}

          <Box mt={6}>
            <Tabs
              variant="simple"
              index={fulfilmentType === "delivery" ? 0 : 1}
              onChange={(index) =>
                setFulfilmentType(index === 0 ? "delivery" : "pickup")
              }
            >
              <TabList
                gap={2}
                bg={softBg}
                border={`1px solid ${borderColor}`}
                mb={4}
              >
                <Tab
                  fontWeight="700"
                  color={mutedText}
                  _selected={{
                    color: pageText,
                    bg: cardBg,
                    borderTop: "2px solid #6ec5c4",
                    boxShadow: cardShadow,
                  }}
                  maxW="100px"
                >
                  Delivery
                </Tab>

                <Tab
                  fontWeight="700"
                  color={mutedText}
                  _selected={{
                    color: pageText,
                    bg: cardBg,
                    borderTop: "2px solid #6ec5c4",
                    boxShadow: cardShadow,
                  }}
                  maxW="100px"
                >
                  Pickup
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0}>
                  {user?.uid ? (
                    <>
                      <Box>
                        <AddressList
                          addresses={addresses}
                          onSelectAddress={setSelectedAddress}
                        />
                      </Box>

                      <Box mt={4}>
                        {!showAddAddress && (
                          <Button
                            {...secondaryButtonStyles}
                            size="sm"
                            onClick={openAndFocusAddressForm}
                          >
                            + Add new address
                          </Button>
                        )}
                      </Box>

                      {showAddAddress && (
                        <Box
                          ref={addAddressFormRef}
                          mt={4}
                          p={4}
                          border="1px solid"
                          borderColor={
                            highlightAddAddressForm
                              ? theme.brand?.electricBlue || theme.primary
                              : borderColor
                          }
                          borderRadius={cardsBorderRadii}
                          bg={softBg}
                          boxShadow={
                            highlightAddAddressForm
                              ? `0 0 0 4px ${isDark ? "rgba(110,197,196,0.20)" : "rgba(49,151,149,0.18)"}`
                              : undefined
                          }
                          transition="all 0.25s ease"
                        >
                          <AddAddressFlow
                            onDone={() => setShowAddAddress(false)}
                          />
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box
                      ref={addAddressFormRef}
                      mt={2}
                      p={4}
                      border="1px solid"
                      borderColor={
                        highlightAddAddressForm
                          ? theme.brand?.electricBlue || theme.primary
                          : borderColor
                      }
                      borderRadius={cardsBorderRadii}
                      bg={softBg}
                      boxShadow={
                        highlightAddAddressForm
                          ? `0 0 0 4px ${isDark ? "rgba(110,197,196,0.20)" : "rgba(49,151,149,0.18)"}`
                          : undefined
                      }
                      transition="all 0.25s ease"
                    >
                      <VStack spacing={4} align="stretch">
                        {selectedAddress && (
                          <Box
                            p={3}
                            borderRadius={cardsBorderRadii}
                            border="1px solid"
                            borderColor="green.300"
                            bg={isDark ? "rgba(72,187,120,0.10)" : "green.50"}
                          >
                            <Text color={pageText} fontWeight="800">
                              Confirmed delivery address
                            </Text>
                            <Text mt={1} color={mutedText} fontSize="sm">
                              {selectedAddress.addressLine}
                            </Text>

                            <Text mt={2} color={mutedText} fontSize="xs">
                              You can edit the address below. Select the updated
                              address from the suggestions, then confirm the
                              changes.
                            </Text>
                          </Box>
                        )}
                        <FormControl isRequired>
                          <FormLabel color={pageText}>
                            Delivery address
                          </FormLabel>

                          <SelectedAddressAutocomplete
                            key={guestAddressInputKey}
                            placeholder="Search delivery address"
                            initialValue={guestAddressLine}
                            onInputChanged={handleGuestAddressInputChanged}
                            onAddressSelected={handleGuestAddressSelected}
                            inputStyles={{
                              bg: cardBg,
                              color: pageText,
                              borderColor:
                                guestAddressTouched &&
                                !isGuestAddressSelectionReady
                                  ? "red.400"
                                  : borderColor,
                              _placeholder: { color: mutedText },
                              _hover: {
                                borderColor:
                                  guestAddressTouched &&
                                  !isGuestAddressSelectionReady
                                    ? "red.400"
                                    : borderStrong,
                              },
                              _focus: {
                                borderColor:
                                  guestAddressTouched &&
                                  !isGuestAddressSelectionReady
                                    ? "red.400"
                                    : borderStrong,
                                boxShadow:
                                  guestAddressTouched &&
                                  !isGuestAddressSelectionReady
                                    ? "0 0 0 1px red"
                                    : `0 0 0 1px ${borderStrong}`,
                              },
                            }}
                          />

                          {isGuestAddressSelectionReady && !selectedAddress ? (
                            <Box
                              mt={3}
                              p={3}
                              borderRadius={cardsBorderRadii}
                              border="1px solid"
                              borderColor="green.300"
                              bg={isDark ? "rgba(72,187,120,0.10)" : "green.50"}
                            >
                              <Badge
                                colorScheme="green"
                                borderRadius={cardsBorderRadii}
                              >
                                Address selected
                              </Badge>
                              <Text mt={2} color={pageText} fontWeight="700">
                                {guestAddressLine}
                              </Text>
                              <Text mt={1} color={mutedText} fontSize="sm">
                                Press Confirm delivery address to use this exact
                                location for the order.
                              </Text>
                            </Box>
                          ) : !selectedAddress ? (
                            <Text
                              mt={2}
                              color={
                                guestAddressTouched ? "red.500" : mutedText
                              }
                              fontSize="sm"
                            >
                              Select a complete address from the suggestions.
                              Typed text by itself is not accepted.
                            </Text>
                          ) : null}
                        </FormControl>

                        <Button
                          color={theme.textOnPrimary}
                          bgImage={theme.gradients.primaryButton}
                          _hover={{
                            bgImage: theme.gradients.primaryButtonHover,
                          }}
                          boxShadow={theme.shadowSm}
                          onClick={useGuestAddressForOrder}
                          isDisabled={
                            !isGuestAddressSelectionReady ||
                            (Boolean(selectedAddress) &&
                              !guestAddressEditingExisting)
                          }
                          _disabled={{
                            opacity: 0.6,
                            cursor: "not-allowed",
                          }}
                        >
                          {guestAddressEditingExisting
                            ? "Confirm changes"
                            : selectedAddress
                              ? "Delivery address confirmed"
                              : "Confirm delivery address"}
                        </Button>
                      </VStack>
                    </Box>
                  )}
                </TabPanel>
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    {sellerCheckoutSummaries.map((summary) => (
                      <Box
                        key={
                          summary.group.sellerUserId || summary.group.sellerName
                        }
                        p={4}
                        borderRadius={cardsBorderRadii}
                        bg={softBg}
                        border={`1px solid ${borderColor}`}
                      >
                        <Flex
                          justify="space-between"
                          align="start"
                          gap={3}
                          wrap="wrap"
                        >
                          <Box>
                            <Badge
                              colorScheme="teal"
                              mb={2}
                              px={3}
                              py={1}
                              borderRadius={cardsBorderRadii}
                            >
                              Seller Pickup
                            </Badge>
                            <Text fontSize="sm" color={mutedText}>
                              {summary.group.items.length} item(s)
                            </Text>
                          </Box>

                          {summary.selectedPickupWarehouse ? (
                            <Badge
                              colorScheme="green"
                              borderRadius={cardsBorderRadii}
                            >
                              Ready
                            </Badge>
                          ) : (
                            <Badge
                              colorScheme="orange"
                              borderRadius={cardsBorderRadii}
                            >
                              Action Required
                            </Badge>
                          )}
                        </Flex>

                        {summary.pickupWarehouses.length === 0 ? (
                          <Box mt={4}>
                            <Text fontWeight="700" color={pageText}>
                              {summary.warehouseMessage ||
                                "No pickup warehouses available"}
                            </Text>
                            <Text mt={1} fontSize="sm" color={mutedText}>
                              This seller needs at least one active
                              pickup-enabled warehouse.
                            </Text>
                          </Box>
                        ) : (
                          <VStack mt={4} spacing={3} align="stretch">
                            {summary.pickupWarehouses.map((warehouse) => {
                              const isSelected =
                                selectedPickupIds[
                                  summary.group.sellerUserId
                                ] === warehouse.id ||
                                (!selectedPickupIds[
                                  summary.group.sellerUserId
                                ] &&
                                  summary.selectedPickupWarehouse?.id ===
                                    warehouse.id);

                              return (
                                <Box
                                  key={warehouse.id}
                                  p={4}
                                  borderRadius={cardsBorderRadii}
                                  bg={cardBg}
                                  border={`1px solid ${isSelected ? borderStrong : borderColor}`}
                                >
                                  <Flex
                                    justify="space-between"
                                    align={{ base: "start", md: "center" }}
                                    direction={{ base: "column", md: "row" }}
                                    gap={3}
                                  >
                                    <HStack align="start" spacing={3} flex="1">
                                      <input
                                        type="radio"
                                        name={`pickupWarehouse-${summary.group.sellerUserId}`}
                                        checked={isSelected}
                                        onChange={() =>
                                          setSelectedPickupIds((prev) => ({
                                            ...prev,
                                            [summary.group.sellerUserId]:
                                              warehouse.id,
                                          }))
                                        }
                                        style={{
                                          marginTop: "4px",
                                          width: "16px",
                                          height: "16px",
                                          cursor: "pointer",
                                        }}
                                      />

                                      <Box flex="1">
                                        <Text fontWeight="700" color={pageText}>
                                          {warehouse.name}
                                        </Text>

                                        {!!warehouse.description && (
                                          <Text
                                            fontSize="sm"
                                            color={mutedText}
                                            mt={1}
                                          >
                                            {warehouse.description}
                                          </Text>
                                        )}

                                        <Text
                                          fontSize="sm"
                                          color={pageText}
                                          mt={1}
                                        >
                                          {warehouse.address}
                                        </Text>

                                        {warehouse.isDefaultDispatch && (
                                          <Badge
                                            mt={2}
                                            colorScheme="green"
                                            borderRadius={cardsBorderRadii}
                                          >
                                            Default Dispatch
                                          </Badge>
                                        )}
                                      </Box>
                                    </HStack>

                                    <ChakraLink
                                      href={`https://www.google.com/maps?q=${warehouse.lat},${warehouse.lng}`}
                                      isExternal
                                      _hover={{ textDecoration: "none" }}
                                    >
                                      <Button
                                        size="sm"
                                        {...secondaryButtonStyles}
                                      >
                                        View map
                                      </Button>
                                    </ChakraLink>
                                  </Flex>
                                </Box>
                              );
                            })}
                          </VStack>
                        )}
                      </Box>
                    ))}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Collapse>
      </Box>

      <Box
        flex={{ base: "1", xl: "0 0 550px" }}
        w={{ base: "100%", xl: "550px" }}
        maxW="100%"
        minW={0}
        bg={cardBg}
        p={5}
        borderRadius={cardsBorderRadii}
        boxShadow={panelShadow}
        border={`1px solid ${borderColor}`}
        backdropFilter={isDark ? "blur(8px)" : undefined}
      >
        <Heading size="md" color={pageText} mb={4}>
          Pay Now
        </Heading>

        {showCustomerDetailsActionPrompt ? (
          <Box
            p={5}
            borderRadius={cardsBorderRadii}
            bg={softBg}
            border={`1px solid ${borderColor}`}
          >
            <Badge
              colorScheme="orange"
              mb={3}
              px={3}
              py={1}
              borderRadius={cardsBorderRadii}
            >
              Action Required
            </Badge>

            <Text fontWeight="700" color={pageText}>
              Add customer details to continue
            </Text>

            <Text mt={2} fontSize="sm" color={mutedText}>
              {customerDetailsPendingMessage}. Add a name and valid WhatsApp
              number before payment so order updates can be sent correctly.
            </Text>

            <Button
              mt={4}
              size="sm"
              color={theme.textOnPrimary}
              bgImage={theme.gradients.primaryButton}
              _hover={{ bgImage: theme.gradients.primaryButtonHover }}
              boxShadow={theme.shadowSm}
              onClick={openAndFocusCustomerDetails}
            >
              Add customer details
            </Button>
          </Box>
        ) : showDeliveryActionPrompt ? (
          <Box
            p={5}
            borderRadius={cardsBorderRadii}
            bg={softBg}
            border={`1px solid ${borderColor}`}
          >
            <Badge
              colorScheme="orange"
              mb={3}
              px={3}
              py={1}
              borderRadius={cardsBorderRadii}
            >
              Action Required
            </Badge>

            <Text fontWeight="700" color={pageText}>
              {fulfilmentType === "delivery" && !hasDeliveryAddress
                ? "Set a delivery address to continue"
                : fulfilmentType === "pickup"
                  ? "Some seller pickup warehouses are not ready"
                  : "Some seller warehouses are not ready"}
            </Text>

            <Text mt={2} fontSize="sm" color={mutedText}>
              {fulfilmentType === "delivery" && !hasDeliveryAddress
                ? "We’ll calculate delivery fees for each seller once you select or add a delivery address."
                : fulfilmentType === "pickup"
                  ? "Every seller order needs an active pickup-enabled warehouse before payment can continue."
                  : "Every seller order needs an active dispatch warehouse before payment can continue."}
            </Text>

            {fulfilmentType === "delivery" && !hasDeliveryAddress && (
              <Button
                mt={4}
                size="sm"
                color={theme.textOnPrimary}
                bgImage={theme.gradients.primaryButton}
                _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                boxShadow={theme.shadowSm}
                onClick={openAndFocusAddressForm}
              >
                Set delivery address
              </Button>
            )}
          </Box>
        ) : (
          <PaymentCheckoutTest
            amount={payableAmount}
            onCreateOrder={createOrder}
            isLoading={warehousesLoading}
            isDisabled={paymentActionDisabled}
          />
        )}

        <Box
          mt={5}
          borderWidth="1px"
          borderColor={borderColor}
          bg={cardBg}
          borderRadius={cardsBorderRadii}
          boxShadow={cardShadow}
          overflow="hidden"
          p={4}
        >
          {fulfilmentType !== "delivery" && (
            <Box
              p={4}
              borderRadius={cardsBorderRadii}
              border={`1px solid ${borderColor}`}
              bg={softBg}
            >
              <Text color={pageText} fontWeight="700">
                Pickup selected
              </Text>
              <Text mt={1} color={mutedText} fontSize="sm">
                The route map is shown for delivery checkout. Pickup locations
                are shown in the checkout details section.
              </Text>
            </Box>
          )}

          {fulfilmentType === "delivery" && !directions && (
            <Box
              p={4}
              borderRadius={cardsBorderRadii}
              border={`1px solid ${borderColor}`}
              bg={softBg}
            >
              <Text color={pageText} fontWeight="700">
                Route map unavailable
              </Text>
              <Text mt={1} color={mutedText} fontSize="sm">
                Select a delivery address and make sure the seller has an active
                dispatch warehouse to load directions.
              </Text>
            </Box>
          )}

          {fulfilmentType === "delivery" && directions && (
            <Box w="100%">
              <VStack
                w="100%"
                align="stretch"
                mb={4}
                spacing={3}
                p={4}
                borderRadius={cardsBorderRadii}
                border={`1px solid ${borderColor}`}
                bg={softBg}
                boxShadow={cardShadow}
              >
                <Flex
                  align="center"
                  justify="space-between"
                  gap={3}
                  wrap="wrap"
                >
                  <Box>
                    <Badge
                      colorScheme="teal"
                      mb={2}
                      px={3}
                      py={1}
                      borderRadius={cardsBorderRadii}
                    >
                      Route Summary
                    </Badge>

                    <Text
                      color={pageText}
                      fontWeight="800"
                      fontSize={{ base: "md", md: "lg" }}
                    >
                      Seller dispatch to delivery address
                    </Text>
                  </Box>

                  {distance && (
                    <Badge
                      colorScheme="green"
                      borderRadius={cardsBorderRadii}
                      px={3}
                      py={1}
                    >
                      {distance}
                    </Badge>
                  )}
                </Flex>

                <Box
                  p={4}
                  borderRadius={cardsBorderRadii}
                  bg={cardBg}
                  border={`1px solid ${borderColor}`}
                >
                  <VStack align="stretch" spacing={3}>
                    <Flex align="start" gap={3}>
                      <Box
                        minW="10px"
                        h="10px"
                        borderRadius={cardsBorderRadii}
                        bg="green.400"
                        mt="7px"
                      />

                      <Box flex="1">
                        <Text
                          color={mutedText}
                          fontSize="xs"
                          fontWeight="700"
                          textTransform="uppercase"
                        >
                          From
                        </Text>
                        <Text color={pageText} fontWeight="700" fontSize="sm">
                          {routeWarehouse?.address ||
                            "Seller dispatch warehouse"}
                        </Text>
                      </Box>
                    </Flex>

                    <Box
                      ml="4px"
                      h="24px"
                      borderLeft={`2px dashed ${borderColor}`}
                    />

                    <Flex align="start" gap={3}>
                      <Box
                        minW="10px"
                        h="10px"
                        borderRadius={cardsBorderRadii}
                        bg="red.400"
                        mt="7px"
                      />

                      <Box flex="1">
                        <Text
                          color={mutedText}
                          fontSize="xs"
                          fontWeight="700"
                          textTransform="uppercase"
                        >
                          To
                        </Text>
                        <Text color={pageText} fontWeight="700" fontSize="sm">
                          {selectedAddress?.addressLine || "Delivery address"}
                        </Text>
                      </Box>
                    </Flex>
                  </VStack>
                </Box>

                <Flex gap={3} direction={{ base: "column", sm: "row" }}>
                  <Box
                    flex="1"
                    p={4}
                    borderRadius={cardsBorderRadii}
                    bg={cardBg}
                    border={`1px solid ${borderColor}`}
                  >
                    <Text
                      color={mutedText}
                      fontSize="xs"
                      fontWeight="700"
                      textTransform="uppercase"
                    >
                      Duration
                    </Text>
                    <Text
                      color={pageText}
                      fontWeight="800"
                      fontSize="md"
                      mt={1}
                    >
                      {duration || "—"}
                    </Text>
                  </Box>

                  <Box
                    flex="1"
                    p={4}
                    borderRadius={cardsBorderRadii}
                    bg={cardBg}
                    border={`1px solid ${borderColor}`}
                  >
                    <Text
                      color={mutedText}
                      fontSize="xs"
                      fontWeight="700"
                      textTransform="uppercase"
                    >
                      Delivery Fee
                    </Text>
                    <Text
                      color={pageText}
                      fontWeight="800"
                      fontSize="md"
                      mt={1}
                    >
                      MWK{" "}
                      {Number(
                        deliveryRouteSummary?.pricingBreakdown.deliveryFee || 0,
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </Box>
                </Flex>

                <Box
                  p={4}
                  borderRadius={cardsBorderRadii}
                  bg={cardBg}
                  border={`1px solid ${borderColor}`}
                  w="100%"
                  maxW="100%"
                  minW={0}
                >
                  <Text
                    color={mutedText}
                    fontSize="xs"
                    fontWeight="700"
                    textTransform="uppercase"
                  >
                    Estimated delivery
                  </Text>

                  <Text
                    mt={1}
                    color={pageText}
                    fontSize="md"
                    fontWeight="800"
                    overflowWrap="anywhere"
                  >
                    {estimatedDeliveryRange.label}
                  </Text>

                  <Text mt={1} color={mutedText} fontSize="sm">
                    Get it in approximately {DELIVERY_ESTIMATE_MIN_DAYS}-
                    {DELIVERY_ESTIMATE_MAX_DAYS} days after purchase.
                  </Text>
                </Box>

                {sellerGroups.length > 1 && (
                  <Box
                    p={3}
                    borderRadius={cardsBorderRadii}
                    bg={isDark ? "rgba(56, 178, 172, 0.08)" : "teal.50"}
                    border={`1px solid ${borderColor}`}
                  >
                    <Text color={mutedText} fontSize="sm">
                      Showing one dispatch route on the map. The checkout
                      delivery total still includes all seller delivery
                      packages.
                    </Text>
                  </Box>
                )}
              </VStack>

              <MapDisplay
                height="440px"
                directions={directions}
                center={center}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Flex>
  );
};

export default CheckoutPage;